import { Component, OnInit, OnDestroy, ViewEncapsulation, HostListener } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { firstValueFrom, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import parsePhoneNumberFromString from 'libphonenumber-js/max';
import { CountryCodeService, Country } from 'an-country-code-selector';
import { TranslateService } from '@ngx-translate/core';

import { AppSettings } from '../../config/config';
import { apiConstants } from '../constant/apiConstant';
import { GroupService } from '../groups/groups.service';
import { HeaderService } from '../header/header.service';
import { commonFunctionService } from '../utils/commonFunctions';
import { StudentProfileViewService } from '../student-profile-view/student-profile-view.service';
import { StudentBasicInfoService } from '../student-basic-info/student-basic-info-service';
import { StudentContactInfoService } from '../student-contact-info/student-contact-info-service';
import { StudentTransportSettingsService } from '../student-transport-settings/student-transport-settings-service';
import { StudentDeactivationService } from '../student-deactivate/student-deactivate.service';
import { FormValidator } from '../form.validator';
import { BrandService } from '../brand/brand.service';

declare var customToastr: any;
declare let moment_js: any;
/* intlTelInput (jQuery plugin) is loaded globally via CDN in index.html for
   the whole app (same as admission-v2 uses) — used ONLY for the Basic Info
   popup's country-code phone input, to match admission-v2's picker exactly
   per explicit instruction. Everything else in groups-v2 stays jQuery-free. */
declare var $: any;

/**
 * GroupsV2Component
 *
 * Modern (non-jQuery) re-skin of the "Manage Groups" / students list page
 * (see src/app/groups/groups.component.ts|html|css for the original).
 *
 * IMPORTANT: this component does NOT modify the original groups.component at
 * all. It duplicates the exact same backend request shapes (endpoint URLs,
 * body field names/casing, header names) so it is a pure presentation-layer
 * re-implementation with no business-logic changes. It uses Angular
 * HttpClient / RxJS only - no jQuery is used anywhere in this file.
 */

interface AcademicYearItem {
  pkAcademicId: number;
  Year: string;
  InActive: number;
  current?: boolean;
  notstarted?: boolean;
  completed?: boolean;
}

interface CategoryItem {
  categoryId: number;
  categoryName: string;
}

interface ClassItem {
  classid: number;
  classname: string;
}

interface SectionItem {
  claaSectionId: number;
  classSection: string;
}

interface StudentRow {
  DT_RowId?: string;
  rollNumber?: any;
  registrationId?: string;
  studentName?: string;
  studentImage?: string;
  parentName?: string;
  motherName?: string;
  stdClassName?: string;
  mobileNumber?: string;
  dateOfAdmission?: string;
  dateOfBirth?: string;
  gender?: number;
  isAppUser?: number;
  fatherAlternateCountryCode?: string;
  fatherAlternateNumber?: string;
  fatherWhatsappCountryCode?: string;
  fatherWhatsappNumber?: string;
  motherAlternateCountryCode?: string;
  motherAlternateNumber?: string;
  motherWhatsappCountryCode?: string;
  motherWhatsappNumber?: string;
  guardianAlternateCountryCode?: string;
  guardianAlternateNumber?: string;
  guardianWhatsappCountryCode?: string;
  guardianWhatsappNumber?: string;
  [key: string]: any;
}

type SortColumnKey = 'registrationId' | 'studentName' | 'dateOfAdmission';

// DataTables column index mapping used by the original /studentlist columns
// array in groups.component.ts (initDataTableServerProcess). We only expose
// sorting for the 3 columns the design calls out, but we keep the exact same
// column index numbers so the "sort" payload shape matches what the backend
// already expects from the legacy DataTable.
const SORT_COLUMN_INDEX: Record<SortColumnKey, number> = {
  registrationId: 2,
  studentName: 3,
  dateOfAdmission: 8,
};

interface StaffRow {
  DT_RowId?: string;
  staffId?: number;
  staffName?: string;
  staffImage?: string;
  employmentId?: string;
  designation?: string;
  emailId?: string;
  mobileNumber?: string;
  gender?: number;
  isAppUser?: number;
  [key: string]: any;
}

interface DesignationItem {
  designationId: number;
  designation: string;
}

interface QualificationItem {
  pkStaffQualificationId: number;
  qualification: string;
}

type StaffSortColumnKey = 'staffName' | 'employmentId' | 'designation';

// Column indexes match the `columns:` array order in groups.component.ts's
// initDataTableServerProcessStaffs (staffName, employmentId, designation,
// emailId, mobileNumber, gender, isAppUser) so the "sort" payload shape
// matches what /stafflist/searchStaff already expects.
const STAFF_SORT_COLUMN_INDEX: Record<StaffSortColumnKey, number> = {
  staffName: 0,
  employmentId: 1,
  designation: 2,
};

@Component({
  selector: 'app-groups-v2',
  templateUrl: './groups-v2.component.html',
  styleUrls: [
    './../../assets/css/theme-default/bootstrap.css',
    './../../assets/css/theme-default/materialadmin.css',
    './../../assets/css/theme-default/font-awesome.min.css',
    './../../assets/css/theme-default/material-design-iconic-font.min.css',
    './groups-v2.component.css'],
  encapsulation: ViewEncapsulation.None,
  providers: [GroupService, HeaderService, StudentProfileViewService, StudentBasicInfoService, StudentContactInfoService, StudentTransportSettingsService, StudentDeactivationService, FormValidator],
})
export class GroupsV2Component implements OnInit, OnDestroy {

  constructor(
    private http: HttpClient,
    private groupservice: GroupService,
    private headerservice: HeaderService,
    private commonFunction: commonFunctionService,
    private router: Router,
    private studentProfileViewService: StudentProfileViewService,
    private studentBasicInfoService: StudentBasicInfoService,
    private studentContactInfoService: StudentContactInfoService,
    private studentTransportOptionsService: StudentTransportSettingsService,
    private studentDeactivationService: StudentDeactivationService,
    private formValidator: FormValidator,
    private sanitizer: DomSanitizer,
    private countryCodeService: CountryCodeService,
    private translate: TranslateService,
    private brandService: BrandService
  ) {}

  brand = this.brandService.brand;

  // Angular templates can't pipe a translation inside an event-binding
  // (click) expression's arguments — `| translate` is only valid in
  // interpolation/property-binding contexts. previewBasicInfoDoc()'s label
  // argument needs the translated string at click time, so it's exposed as
  // a plain getter here instead of inlining the pipe in the template.
  get nationalIdCopyLabel(): string {
    return this.translate.instant(this.brand.nationalIdCopyLabelKey);
  }

  // ------------------------------------------------------------------
  // Tabs
  // ------------------------------------------------------------------
  activeTab: 'student' | 'staff' = 'student';

  // ------------------------------------------------------------------
  // Auth / school context
  // ------------------------------------------------------------------
  getSchoolId = localStorage.getItem('schoolinfo');
  getAuthToken = localStorage.getItem('usertoken');
  permissions: any = {};

  // ------------------------------------------------------------------
  // Academic year
  // ------------------------------------------------------------------
  academicList: AcademicYearItem[] = [];
  currentAcademicId: number | null = null;
  currentAcademicYearLabel = '';
  currentAcademicState: 'RUNNING' | 'NOT_STARTED' | 'COMPLETED' | '' = '';

  // Extension-service enablement flags — same localStorage keys header-v2
  // reads to gate its floating ID Card / TC buttons (groups.component.html
  // has no equivalent inline gating; that button lives in the shared header).
  idCardServiceEnabled = false;
  transferCertificateEnabled = false;
  staffIdCardServiceEnabled = false;

  get isAcademicRunning(): boolean {
    return this.currentAcademicState === 'RUNNING';
  }

  // ------------------------------------------------------------------
  // Filters (category / class / section)
  // ------------------------------------------------------------------
  schoolCategories: CategoryItem[] = [];
  schoolClasses: ClassItem[] = [];
  schoolClassesSections: SectionItem[] = [];

  selectedCategory: any = 0;
  selectedClass: any = 0;
  selectedSection: any = 0;

  classDropdownDisabled = true;
  sectionDropdownDisabled = true;

  // ------------------------------------------------------------------
  // Table state
  // ------------------------------------------------------------------
  students: StudentRow[] = [];
  totalRecords = 0;
  /* Starts true so the skeleton shows immediately on first render, instead
     of a blank table while the academic-year bootstrap call (a prerequisite
     for fetchStudents) is still in flight. */
  loadingStudents = true;

  pageIndex = 0; // zero based
  pageSize = 25;
  readonly pageSizeOptions = [10, 25, 50, 100];

  searchTerm = '';
  private searchInput$ = new Subject<string>();

  /* Extra columns hidden by default, toggled from the "Columns" dropdown —
     mirrors the DataTables "colvis" columns from groups.component.ts's
     initDataTableServerProcess (same field keys/order), just without the
     jQuery Buttons/ColVis extension. */
  columnOptions: { field: string; label: string; labelKey: string; visible: boolean }[] = [
    { field: 'rollNumber', label: 'Roll Number', labelKey: 'groupsV2.colRollNumber', visible: false },
    { field: 'motherName', label: 'Mother Name', labelKey: 'groupsV2.colMotherName', visible: false },
    { field: 'dateOfBirth', label: 'Date of Birth', labelKey: 'groupsV2.colDateOfBirth', visible: false },
    { field: 'gender', label: 'Gender', labelKey: 'groupsV2.colGender', visible: false },
    { field: 'isAppUser', label: 'App User', labelKey: 'groupsV2.colAppUser', visible: false },
    { field: 'emailId', label: 'Email', labelKey: 'groupsV2.colEmail', visible: false },
    { field: 'surname', label: 'Surname', labelKey: 'groupsV2.colSurname', visible: false },
    { field: 'address', label: 'Address', labelKey: 'groupsV2.colAddress', visible: false },
    { field: 'guardianName', label: 'Guardian Name', labelKey: 'groupsV2.colGuardianName', visible: false },
    { field: 'aadharNumber', label: 'Aadhar Number', labelKey: 'groupsV2.colAadharNumber', visible: false },
    { field: 'houseName', label: 'House Name', labelKey: 'groupsV2.colHouseName', visible: false },
    { field: 'bloodGroup', label: 'Blood Group', labelKey: 'groupsV2.colBloodGroup', visible: false },
    { field: 'tcOrBirthCertificateUploaded', label: 'TC/Birth Certificate', labelKey: 'groupsV2.colTcOrBirthCertificate', visible: false },
    { field: 'busName', label: 'Bus Name', labelKey: 'groupsV2.colBusName', visible: false },
    { field: 'busStopName', label: 'Bus Stop', labelKey: 'groupsV2.colBusStop', visible: false },
    { field: 'fatherAlternateNumber', label: "Father's Alternate Mobile", labelKey: 'groupsV2.colFatherAlternateMobile', visible: false },
    { field: 'fatherWhatsappNumber', label: "Father's WhatsApp Number", labelKey: 'groupsV2.colFatherWhatsappNumber', visible: false },
    { field: 'motherAlternateNumber', label: "Mother's Alternate Mobile", labelKey: 'groupsV2.colMotherAlternateMobile', visible: false },
    { field: 'motherWhatsappNumber', label: "Mother's WhatsApp Number", labelKey: 'groupsV2.colMotherWhatsappNumber', visible: false },
    { field: 'guardianAlternateNumber', label: "Guardian's Alternate Mobile", labelKey: 'groupsV2.colGuardianAlternateMobile', visible: false },
    { field: 'guardianWhatsappNumber', label: "Guardian's WhatsApp Number", labelKey: 'groupsV2.colGuardianWhatsappNumber', visible: false },
  ];
  columnDropdownOpen = false;

  toggleColumnDropdown(): void {
    this.columnDropdownOpen = !this.columnDropdownOpen;
  }

  toggleColumnVisibility(col: { field: string; visible: boolean }): void {
    // Matches the old component's DataTables colvis behavior — any number
    // of optional columns can be shown at once, each toggles independently.
    col.visible = !col.visible;
  }

  isColumnVisible(field: string): boolean {
    return this.columnOptions.find(c => c.field === field)?.visible ?? false;
  }

  get visibleColumnCount(): number {
    return 9 + this.columnOptions.filter(c => c.visible).length;
  }

  get skeletonColumns(): number[] {
    return Array.from({ length: this.visibleColumnCount });
  }

  get studentsPaginatorReportTemplate(): string {
    return this.translate.instant('groupsV2.showingStudentsReport');
  }

  get staffsPaginatorReportTemplate(): string {
    return this.translate.instant('groupsV2.showingStaffsReport');
  }

  get skeletonRows(): number[] {
    return this.loadingStudents ? Array.from({ length: 10 }) : [];
  }

  get staffSkeletonColumns(): number[] {
    return Array.from({ length: 8 });
  }

  get staffSkeletonRows(): number[] {
    return this.loadingStaff ? Array.from({ length: 10 }) : [];
  }

  private static readonly PHONE_COLUMN_COUNTRY_CODE_FIELD: { [field: string]: string } = {
    fatherAlternateNumber: 'fatherAlternateCountryCode',
    fatherWhatsappNumber: 'fatherWhatsappCountryCode',
    motherAlternateNumber: 'motherAlternateCountryCode',
    motherWhatsappNumber: 'motherWhatsappCountryCode',
    guardianAlternateNumber: 'guardianAlternateCountryCode',
    guardianWhatsappNumber: 'guardianWhatsappCountryCode',
  };

  formatColumnValue(field: string, row: StudentRow): string {
    const value = row[field];
    if (field === 'gender') {
      return value === 0 ? this.translate.instant('groupsV2.female') : value === 1 ? this.translate.instant('groupsV2.male') : '—';
    }
    if (field === 'isAppUser') {
      return value === 0 ? this.translate.instant('groupsV2.inactive') : value === 1 ? this.translate.instant('groupsV2.active') : '—';
    }
    const countryCodeField = GroupsV2Component.PHONE_COLUMN_COUNTRY_CODE_FIELD[field];
    if (countryCodeField) {
      if (!value) {
        return '—';
      }
      const countryCode = row[countryCodeField];
      return countryCode ? `+${countryCode} ${value}` : value;
    }
    return value ?? '—';
  }

  // ------------------------------------------------------------------
  // Export (Copy / Excel / CSV) — mirrors groups.component.ts's DataTables
  // Buttons extension, which exports only the currently visible page's
  // rows (server-side processing has no full dataset on the client) using
  // the currently visible columns (core columns + whichever extra colvis
  // column is toggled on).
  // ------------------------------------------------------------------
  private getExportTitle(): string {
    const categoryText = this.schoolCategories.find(c => c.categoryId === this.selectedCategory)?.categoryName;
    let text = 'Students export sheet ' + (this.selectedCategory === 0 ? 'All Categories' : (categoryText || ''));
    if (this.selectedClass !== 0) {
      const classText = this.schoolClasses.find(c => c.classid === this.selectedClass)?.classname;
      if (classText) {
        text += '-' + classText;
      }
    }
    if (this.selectedSection !== 0) {
      const sectionText = this.schoolClassesSections.find(s => s.claaSectionId === this.selectedSection)?.classSection;
      if (sectionText) {
        text += '-' + sectionText;
      }
    }
    return text;
  }

  private getExportHeaders(): string[] {
    const headers = ['Registration No', 'Student', 'Parent', 'Class', 'Mobile Number', 'Admission Date'];
    const visibleExtras = this.columnOptions.filter(c => c.visible);
    visibleExtras.forEach(col => headers.push(col.label));
    return headers;
  }

  private getExportRows(): string[][] {
    const visibleExtras = this.columnOptions.filter(c => c.visible);
    return this.students.map(row => {
      const values = [
        row.registrationId ?? '',
        row.studentName ?? '',
        row.parentName ?? '',
        row.stdClassName ?? '',
        row.mobileNumber ?? '',
        row.dateOfAdmission ?? '',
      ];
      visibleExtras.forEach(col => values.push(this.formatColumnValue(col.field, row)));
      return values;
    });
  }

  private toDelimitedText(delimiter: string): string {
    const headers = this.getExportHeaders();
    const rows = this.getExportRows();
    const escapeCell = (cell: string) => {
      if (cell.includes(delimiter) || cell.includes('"') || cell.includes('\n')) {
        return '"' + cell.replace(/"/g, '""') + '"';
      }
      return cell;
    };
    const lines = [headers, ...rows].map(line => line.map(cell => escapeCell(String(cell))).join(delimiter));
    return lines.join('\n');
  }

  private downloadFile(content: string, filename: string, mimeType: string): void {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  exportCopy(): void {
    const text = this.toDelimitedText('\t');
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(
        () => {
          if (typeof customToastr === 'function') {
            customToastr('Copied to clipboard', 'success');
          }
        },
        () => {
          if (typeof customToastr === 'function') {
            customToastr('Failed to copy to clipboard', 'error');
          }
        }
      );
    }
  }

  exportCsv(): void {
    const csv = this.toDelimitedText(',');
    this.downloadFile(csv, this.getExportTitle() + '.csv', 'text/csv;charset=utf-8;');
  }

  exportExcel(): void {
    // Tab-delimited text opens cleanly in Excel without needing a full
    // xlsx-writing library — matches the same data csvHtml5/excelHtml5
    // would have exported (current page, currently visible columns).
    const tsv = this.toDelimitedText('\t');
    this.downloadFile(tsv, this.getExportTitle() + '.xls', 'application/vnd.ms-excel;charset=utf-8;');
  }

  sortColumn: SortColumnKey | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';

  // ------------------------------------------------------------------
  // Row actions - edit dropdown
  // ------------------------------------------------------------------
  openActionMenuForRowId: string | null = null;

  // 4 reusable child popup components - same @Input/@Output contract as
  // groups.component.html (app-student-basic-info, app-student-contact-info,
  // app-student-transport-settings-popup, app-student-deactivate).
  openStudentBasicInfoPopup = false;
  studentBasicInfoStudentId: any;
  studentBasicInfoStudentName: any;

  // Contact Details - native groups-v2 popup that replaces the shared
  // <app-student-contact-info> component (still used unmodified by the OLD
  // groups.component). Reuses StudentContactInfoService's exact endpoints/
  // request shapes and validation rules, so backend behavior is unchanged.
  // Phone country-code pickers use intlTelInput exactly like the Basic Info
  // popup (see initBasicInfoPhoneInput); validity is the same simple
  // digit-shape check groups-v2 already uses for Basic Info's phone field,
  // rather than the shared component's libphonenumber-js check.
  openStudentContactInfoPopup = false;
  studentContactInfoStudentId: any;
  studentContactInfoStudentName: any;
  contactInfoLoading = false;
  contactInfoSaving = false;
  contactInfoData: any = {};

  contactInfoFatherContact = '';
  contactInfoMotherContact = '';
  contactInfoGuardianContact = '';
  contactInfoFatherCountryCode = this.brand.defaultCountryDialCode || '91';
  contactInfoMotherCountryCode = this.brand.defaultCountryDialCode || '91';
  contactInfoGuardianCountryCode = this.brand.defaultCountryDialCode || '91';
  contactInfoInvalidFatherContact = false;
  contactInfoInvalidMotherContact = false;
  contactInfoInvalidGuardianContact = false;
  contactInfoFatherCountry?: Country;
  contactInfoMotherCountry?: Country;
  contactInfoGuardianCountry?: Country;

  contactInfoFatherAltContact = '';
  contactInfoFatherAltCountryCode = this.brand.defaultCountryDialCode || '91';
  contactInfoInvalidFatherAltContact = false;
  contactInfoFatherAltCountry?: Country;
  contactInfoFatherWhatsapp = '';
  contactInfoFatherWhatsappCountryCode = this.brand.defaultCountryDialCode || '91';
  contactInfoInvalidFatherWhatsapp = false;
  contactInfoFatherWhatsappCountry?: Country;

  contactInfoMotherAltContact = '';
  contactInfoMotherAltCountryCode = this.brand.defaultCountryDialCode || '91';
  contactInfoInvalidMotherAltContact = false;
  contactInfoMotherAltCountry?: Country;
  contactInfoMotherWhatsapp = '';
  contactInfoMotherWhatsappCountryCode = this.brand.defaultCountryDialCode || '91';
  contactInfoInvalidMotherWhatsapp = false;
  contactInfoMotherWhatsappCountry?: Country;

  contactInfoGuardianAltContact = '';
  contactInfoGuardianAltCountryCode = this.brand.defaultCountryDialCode || '91';
  contactInfoInvalidGuardianAltContact = false;
  contactInfoGuardianAltCountry?: Country;
  contactInfoGuardianWhatsapp = '';
  contactInfoGuardianWhatsappCountryCode = this.brand.defaultCountryDialCode || '91';
  contactInfoInvalidGuardianWhatsapp = false;
  contactInfoGuardianWhatsappCountry?: Country;

  contactInfoFatherImagePreview: string | null = null;
  contactInfoMotherImagePreview: string | null = null;
  contactInfoGuardianImagePreview: string | null = null;
  private contactInfoFatherImageFile: File | null = null;
  private contactInfoMotherImageFile: File | null = null;
  private contactInfoGuardianImageFile: File | null = null;
  private contactInfoPendingImageDeletions = new Set<string>();

  readonly contactInfoOccupationList: any[] = [
    { name: 'Agriculture, Food and Natural Resources', value: 'Agriculture, Food and Natural Resources' },
    { name: 'Architecture and Construction', value: 'Architecture and Construction' },
    { name: 'Arts, Audio/Video Technology and Communications', value: 'Arts, Audio/Video Technology and Communications' },
    { name: 'Business Management and Administration', value: 'Business Management and Administration' },
    { name: 'Education and Training', value: 'Education and Training' },
    { name: 'Finance', value: 'Finance' },
    { name: 'Government and Public Administration', value: 'Government and Public Administration' },
    { name: 'Health Science', value: 'Health Science' },
    { name: 'Hospitality and Tourism', value: 'Hospitality and Tourism' },
    { name: 'Human Services', value: 'Human Services' },
    { name: 'Information Technology', value: 'Information Technology' },
    { name: 'Law, Public Safety, Corrections and Security', value: 'Law, Public Safety, Corrections and Security' },
    { name: 'Manufacturing', value: 'Manufacturing' },
    { name: 'Marketing, Sales and Service', value: 'Marketing, Sales and Service' },
    { name: 'Science, Technology, Engineering and Mathematics', value: 'Science, Technology, Engineering and Mathematics' },
    { name: 'Transportation, Distribution and Logistics', value: 'Transportation, Distribution and Logistics' },
    { name: 'Others', value: 'Others' },
  ];

  get contactInfoSkeletonRows(): number[] {
    return this.contactInfoLoading ? Array.from({ length: 8 }) : [];
  }

  // Transport Options - native groups-v2 popup that replaces the shared
  // <app-student-transport-settings-popup> component (still used unmodified
  // by the OLD groups.component). Reuses StudentTransportSettingsService's
  // exact endpoints/request shapes and validation, so backend behavior is
  // unchanged. Same visual pattern as the other ported popups (p-dialog,
  // gv2-basicinfo-* header/body/field/grid classes).
  openStudentTransportOptionSettingsPopup = false;
  studentTransportOptionsStudentId: any;
  studentTransportOptionsStudentName: any;
  transportOptionsLoading = false;
  transportOptionsSaving = false;
  transportOptionsStopList: any[] = [];
  transportOptionsBusList: any[] = [];
  transportOptionsSelectedStopId: any = null;
  transportOptionsSelectedBusId: any = null;
  transportOptionsTerms: any[] = [];
  transportOptionsViewTermIds: any[] = [];
  transportOptionsSelectAll = false;

  // Deactivate Student - native groups-v2 popup that replaces the shared
  // <app-student-deactivate> component (still used unmodified by the OLD
  // groups.component). Reuses StudentDeactivationService's exact endpoints/
  // request shapes and validation, so backend behavior is unchanged.
  studentDeactivatePopupStudentId: any;
  studentDeactivatePopupStudentName: any;
  openStudentDeactivatePopup = false;
  deactivateReasons: any[] = [];
  deactivateSelectedReasonId: any = null;
  deactivateReasonsLoading = false;
  deactivateProcessing = false;
  deactivateShowConfirm = false;

  get deactivateReasonGroups(): { description: string; reasons: any[] }[] {
    const groups: { description: string; reasons: any[] }[] = [];
    this.deactivateReasons.forEach((reason) => {
      let group = groups.find((g) => g.description === reason.description);
      if (!group) {
        group = { description: reason.description, reasons: [] };
        groups.push(group);
      }
      group.reasons.push(reason);
    });
    return groups;
  }

  schoolCurrency = localStorage.getItem('currency') || 'INR';

  // Document hub - native groups-v2 modal (list + search + upload + delete +
  // preview), reusing the same GroupService methods the legacy documentHub()
  // flow in groups.component.ts uses.
  openDocumentHubPopup = false;
  documentHubStudentId: any;
  documentHubStudentName: any;
  documentHubRegistrationId: any;
  documentHubUploadedList: any[] = [];
  documentHubData: any[] = [];
  fetchingDocumentHub = false;
  documentHubSearchQuery = '';

  documentHubOpenActionIndex: any = '';
  documentHubPreviewingIndex: any = '';
  documentHubDeleting = false;

  documentHubShowUpload = false;
  documentHubTypesInfo: any[] = [];
  documentHubSelectedType: any = null;
  documentHubFetchingTypes = false;
  documentHubSelectedFile: any = null;
  documentHubUploading = false;

  // Student update history - minimal native modal, reuses
  // groupservice.fetchStudentUpdateHistory().
  openStudentUpdateHistoryPopup = false;
  studentUpdateHistoryStudentName = '';
  studentUpdateHistoryRegistrationId = '';
  studentUpdateHistoryList: any[] = [];
  fetchingStudentUpdateHistory = false;

  get historySkeletonRows(): number[] {
    return this.fetchingStudentUpdateHistory ? Array.from({ length: 6 }) : [];
  }

  /* Purely presentational, client-side only — the already-fetched
     studentUpdateHistoryList is just searched/paged in the browser, no new
     API calls or change to fetchStudentUpdateHistory's request/response. */
  historySearchTerm = '';
  historyPageSize = 10;
  readonly historyPageSizeOptions = [10, 25, 50];
  historyPageIndex = 0;

  get filteredHistoryList(): any[] {
    const term = this.historySearchTerm.trim().toLowerCase();
    if (!term) {
      return this.studentUpdateHistoryList;
    }
    return this.studentUpdateHistoryList.filter(tr => {
      const field = this.formatHistoryColumnName(tr.ColumnName || '').toLowerCase();
      return field.includes(term)
        || String(tr.OldValue ?? '').toLowerCase().includes(term)
        || String(tr.NewValue ?? '').toLowerCase().includes(term)
        || String(tr.UpdatedBy ?? '').toLowerCase().includes(term);
    });
  }

  get historyTotalPages(): number {
    return Math.max(1, Math.ceil(this.filteredHistoryList.length / this.historyPageSize));
  }

  get pagedHistoryList(): any[] {
    const start = this.historyPageIndex * this.historyPageSize;
    return this.filteredHistoryList.slice(start, start + this.historyPageSize);
  }

  get historyShowingFrom(): number {
    return this.filteredHistoryList.length === 0 ? 0 : this.historyPageIndex * this.historyPageSize + 1;
  }

  get historyShowingTo(): number {
    return Math.min(this.filteredHistoryList.length, (this.historyPageIndex + 1) * this.historyPageSize);
  }

  onHistorySearchChange(value: string): void {
    this.historySearchTerm = value;
    this.historyPageIndex = 0;
  }

  onHistoryPageSizeChange(size: number): void {
    this.historyPageSize = size;
    this.historyPageIndex = 0;
  }

  goToHistoryPrevPage(): void {
    if (this.historyPageIndex > 0) {
      this.historyPageIndex--;
    }
  }

  goToHistoryNextPage(): void {
    if (this.historyPageIndex < this.historyTotalPages - 1) {
      this.historyPageIndex++;
    }
  }

  /* Detect by the VALUE itself (any http(s) URL), not just fields whose name
     ends in "Image" — some document fields (e.g. CasteCertificate) also
     store S3 file URLs and should render as a preview, not raw link text. */
  isHistoryUrlValue(value: any): boolean {
    return typeof value === 'string' && /^https?:\/\//i.test(value.trim());
  }

  historyImagePreviewUrl: string | null = null;
  isHistoryImagePreviewOpen = false;

  previewHistoryImage(url: string): void {
    this.historyImagePreviewUrl = url;
    this.isHistoryImagePreviewOpen = true;
  }

  // Shared in-app document preview overlay - matches admission-v2's document
  // preview overlay exactly (image vs PDF iframe). Used by both the Basic
  // Info certificate chips (known URL + known mime type) and the Document
  // Hub (fetched as an authenticated blob, mime type read from the blob),
  // so clicking a document previews it in-app instead of navigating away.
  isDocPreviewOpen = false;
  docPreviewUrl: string | null = null;
  docPreviewSafeUrl: SafeResourceUrl | null = null;
  docPreviewIsPdf = false;
  docPreviewLabel = '';
  private docPreviewObjectUrl: string | null = null;

  previewBasicInfoDoc(url: string, fileType: string, label: string): void {
    if (!url) {
      return;
    }
    const resolvedType = fileType || this.commonFunction.getFileTypeFromUrl(url);
    this.openDocPreview(url, resolvedType === 'application/pdf', label);
  }

  private openDocPreview(url: string, isPdf: boolean, label: string): void {
    this.docPreviewLabel = label;
    this.docPreviewIsPdf = isPdf;
    this.docPreviewUrl = isPdf ? null : url;
    this.docPreviewSafeUrl = isPdf ? this.sanitizer.bypassSecurityTrustResourceUrl(url) : null;
    this.isDocPreviewOpen = true;
  }

  closeDocPreview(): void {
    this.isDocPreviewOpen = false;
    this.docPreviewUrl = null;
    this.docPreviewSafeUrl = null;
    this.docPreviewIsPdf = false;
    this.docPreviewLabel = '';
    if (this.docPreviewObjectUrl) {
      URL.revokeObjectURL(this.docPreviewObjectUrl);
      this.docPreviewObjectUrl = null;
    }
  }

  historyFieldIcon(columnName: string): string {
    const name = (columnName || '').toLowerCase();
    if (name.includes('image')) {
      return 'ri-image-line';
    }
    if (name.includes('name')) {
      return 'ri-user-line';
    }
    return 'ri-edit-2-line';
  }

  // ------------------------------------------------------------------
  // Print / profile view popup — native p-dialog reimplementation of
  // app-student-profile-view's popup (same service call
  // getStudentProfilePrintDetails, same fields, same print-a-new-window
  // behavior), so it can use PrimeNG + a skeleton loader without touching
  // that shared component (still used as-is by the old groups.component).
  // ------------------------------------------------------------------
  openStudentProfileViewPopup = false;
  studentProfileViewStudentId: any;
  studentProfileViewStudentName: any;
  studentProfile: any = {};
  profileAcademicInfo: any = {};
  fetchingStudentProfile = false;

  get profileSkeletonRows(): number[] {
    return this.fetchingStudentProfile ? Array.from({ length: 6 }) : [];
  }

  formatProfileDate(date: any): string {
    if (!date) {
      return '-';
    }
    const d = new Date(date);
    if (isNaN(d.getTime())) {
      return '-';
    }
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${day}-${month}-${d.getFullYear()}`;
  }

  printStudentProfile(): void {
    const container = document.getElementById('gv2-student-profile-print-container');
    if (!container) {
      return;
    }
    const windowObject = window.open('', 'Receipt', 'width=1500,height=1100,top=100,left=150,toolbars=no,scrollbars=yes,status=no,resizable=no');
    if (!windowObject) {
      return;
    }
    /* The print window has no access to the app's global stylesheets, so the
       table/section styling has to be inlined here to match the on-screen
       .gv2-profile-* layout instead of rendering as bare unstyled HTML. */
    const printStyles = `
      body { font-family: 'Plus Jakarta Sans', Arial, sans-serif; color: #232323; padding: 24px; }
      .gv2-profile-header { text-align: center; margin-bottom: 16px; }
      .gv2-profile-header h3 { margin: 4px 0; font-size: 20px; }
      .gv2-profile-header h5 { margin: 4px 0; font-weight: normal; color: #7E7E7E; }
      .gv2-profile-avatar-row { display: flex; justify-content: center; margin: 16px 0; }
      .gv2-profile-avatar { width: 120px; height: 120px; border-radius: 10px; object-fit: cover; }
      .gv2-profile-section-title { font-size: 15px; font-weight: 700; margin: 20px 0 10px; }
      .gv2-profile-table { width: 100%; border-collapse: collapse; font-size: 13px; margin-bottom: 10px; }
      .gv2-profile-table th, .gv2-profile-table td { padding: 6px 10px; text-align: left; vertical-align: top; }
      .gv2-profile-table th { color: #7E7E7E; font-weight: 600; white-space: nowrap; }
    `;
    windowObject.document.writeln(`<html><head><title>${this.studentProfileViewStudentName || 'Student Profile'}</title><style>${printStyles}</style></head><body>${container.innerHTML}</body></html>`);
    windowObject.document.close();
    windowObject.focus();
    // Close the print window once the browser's print dialog is dismissed,
    // whether the user printed or hit Cancel - afterprint fires either way.
    windowObject.onafterprint = () => windowObject.close();
    windowObject.print();
  }

  // ------------------------------------------------------------------
  // Selection + self-contained ID card / TC (Transfer Certificate) features
  // (no dependency on header-v2 - same HeaderService endpoints header-v2's
  // openIdCard()/generateIdCard()/generateTcSelectedStudent()/openTC() call,
  // and the same post-generate navigation to the shared /id-card and /tc
  // pages those methods use, just triggered from groups-v2's own toolbar/
  // modals instead of header-v2's floating button + shared DOM element.)
  // ------------------------------------------------------------------
  selectedStudentIds: number[] = [];
  openIdCardPopup = false;
  idCardTemplates: any[] = [];
  isFetchingIdCard = false;
  isGeneratingId = false;
  isIdCardGenerated = false;

  openTcPopup = false;
  isGeneratingTc = false;
  isTcGenerated = false;

  // Bulk Update Roll Number popup (mirrors groups.component.ts's
  // #update-student-rool-number popup for the currently selected class/section).
  showUpdateRollNumberPopup = false;
  updateRollNumberStudentList: any[] = [];
  filteredRollNumberStudentList: any[] = [];
  updateRollNumberClassName = '';
  updateRollNumberSectionName = '';
  rollNumberSearchText = '';
  updateRollNumberLoading = false;
  savingRollNumbers = false;

  handleKeyDown(event: KeyboardEvent): void {
    this.commonFunction.handleKeyDown(event);
  }

  openUpdateRollNumberPopup(): void {
    const selectedClassInfo: any = this.schoolClasses.find((c: any) => c.classid == this.selectedClass);
    const selectedSectionInfo: any = this.schoolClassesSections.find((s: any) => s.claaSectionId == this.selectedSection);
    this.updateRollNumberClassName = selectedClassInfo ? selectedClassInfo.classname : '';
    this.updateRollNumberSectionName = selectedSectionInfo ? selectedSectionInfo.classSection : '';

    this.rollNumberSearchText = '';
    this.updateRollNumberStudentList = [];
    this.filteredRollNumberStudentList = [];
    this.updateRollNumberLoading = true;
    this.showUpdateRollNumberPopup = true;

    this.groupservice.getClassStudentListForRollNumber(this.selectedSection).subscribe(
      (data: any) => {
        this.updateRollNumberLoading = false;
        const studentList = (data && data.body && data.body.studentList) || [];
        this.updateRollNumberStudentList = studentList.map((student: any) => ({
          studentId: student.studentId,
          studentName: student.studentName,
          studentImage: student.studentImage,
          admissionNumber: student.admissionNumber,
          fatherName: student.parentName,
          mobileNumber: student.mobileNumber,
          rollNumber: student.rollNumber,
        }));
        this.filteredRollNumberStudentList = this.updateRollNumberStudentList;
      },
      (error) => {
        this.updateRollNumberLoading = false;
        const genericErrorTranslation = this.translate.instant('error');
        customToastr(error?.error?.message ? error.error.message : genericErrorTranslation, 'error');
      }
    );
  }

  filterRollNumberStudentList(): void {
    const searchText = (this.rollNumberSearchText || '').trim().toLowerCase();
    if (!searchText) {
      this.filteredRollNumberStudentList = this.updateRollNumberStudentList;
      return;
    }
    this.filteredRollNumberStudentList = this.updateRollNumberStudentList.filter((student: any) =>
      (student.studentName || '').toLowerCase().includes(searchText) ||
      (student.admissionNumber || '').toLowerCase().includes(searchText) ||
      (student.fatherName || '').toLowerCase().includes(searchText) ||
      (student.mobileNumber || '').includes(searchText)
    );
  }

  sanitizeRollNumberOnPaste(event: ClipboardEvent, student: any): void {
    event.preventDefault();
    const pastedText = event.clipboardData ? event.clipboardData.getData('text') : '';
    student.rollNumber = pastedText.replace(/\D/g, '').slice(0, 4);
  }

  sortRollNumberMenuOpen = false;

  toggleSortRollNumberMenu(): void {
    this.sortRollNumberMenuOpen = !this.sortRollNumberMenuOpen;
  }

  sortRollNumbersBy(criteria: 'name' | 'admissionNumber'): void {
    this.sortRollNumberMenuOpen = false;

    if (criteria === 'admissionNumber') {
      this.updateRollNumberStudentList.sort((a: any, b: any) =>
        (a.admissionNumber || '').localeCompare(b.admissionNumber || '', undefined, { numeric: true, sensitivity: 'base' })
      );
    } else {
      this.updateRollNumberStudentList.sort((a: any, b: any) =>
        (a.studentName || '').localeCompare(b.studentName || '', undefined, { sensitivity: 'base' })
      );
    }

    this.updateRollNumberStudentList.forEach((student: any, index: number) => {
      student.rollNumber = String(index + 1);
    });
    this.filterRollNumberStudentList();
  }

  hasEmptyRollNumber(): boolean {
    return this.updateRollNumberStudentList.some((student: any) => !student.rollNumber);
  }

  saveRollNumbers(): void {
    if (this.hasEmptyRollNumber()) {
      return;
    }

    const studentList = this.updateRollNumberStudentList.map((student: any) => ({
      studentId: student.studentId,
      rollNumber: student.rollNumber,
    }));

    this.savingRollNumbers = true;
    this.groupservice.bulkUpdateStudentRollNumber(this.selectedSection, studentList).subscribe(
      () => {
        this.savingRollNumbers = false;
        const successTranslation = this.translate.instant('groupsV2.rollNumbersUpdatedSuccessfully');
        customToastr(successTranslation, 'success');
        this.showUpdateRollNumberPopup = false;
        this.fetchStudents();
      },
      (error) => {
        this.savingRollNumbers = false;
        const genericErrorTranslation = this.translate.instant('error');
        customToastr(error?.error?.message ? error.error.message : genericErrorTranslation, 'error');
      }
    );
  }

  closeUpdateRollNumberPopup(): void {
    this.showUpdateRollNumberPopup = false;
  }

  ngOnInit(): void {
    this.searchInput$.pipe(debounceTime(400)).subscribe((value) => {
      this.searchTerm = value;
      this.pageIndex = 0;
      this.fetchStudents();
    });

    this.staffSearchInput$.pipe(debounceTime(400)).subscribe((value) => {
      this.staffSearchTerm = value;
      this.staffPageIndex = 0;
      this.fetchStaff();
    });

    const adminPermissionlist = localStorage.getItem('adminPermissionlist');
    if (adminPermissionlist) {
      try {
        this.permissions = JSON.parse(adminPermissionlist);
      } catch (e) {
        this.permissions = {};
      }
      if (this.permissions.SCHOOL_STUDENT_MANAGEMENT !== 'ENABLED' && this.permissions.SCHOOL_STAFFS_MANAGEMENT === 'ENABLED') {
        this.activeTab = 'staff';
      }
    }

    // Same extension-service flags header-v2.component.ts reads to gate its
    // floating ID Card / TC buttons (localStorage['idCardService'] etc.,
    // set from the school's extensionServices permissions on login).
    this.loadExtensionServiceFlags();

    this.bootstrap();
    this.getSchoolDesignationList();
    this.getStaffQualificationList();
  }

  ngOnDestroy(): void {
    this.searchInput$.complete();
    this.staffSearchInput$.complete();
  }

  private getCookieValue(name: string): string | null {
    const cookies = document.cookie.split('; ');
    for (const cookie of cookies) {
      const [key, value] = cookie.split('=');
      if (key === name) {
        return decodeURIComponent(value);
      }
    }
    return null;
  }

  // Same first-login race documented in header-v2.component.ts: menu.component
  // derives idCardService/transferCertificate/staffIdCardService from the
  // extensionServices cookie and writes them to localStorage, but groups-v2
  // can mount before that write happens. Re-derive from extensionServices
  // directly in that case so the ID Card / TC buttons don't need a refresh.
  private loadExtensionServiceFlags(): void {
    this.idCardServiceEnabled = localStorage.getItem('idCardService') === 'true';
    this.transferCertificateEnabled = localStorage.getItem('transferCertificate') === 'true';
    this.staffIdCardServiceEnabled = localStorage.getItem('staffIdCardService') === 'true';

    if (!localStorage.getItem('idCardService') && !localStorage.getItem('transferCertificate') && !localStorage.getItem('staffIdCardService')) {
      const extensionServicesRaw = localStorage.getItem('extensionServices') || this.getCookieValue('extensionServices');
      if (extensionServicesRaw) {
        try {
          const servicesArray = JSON.parse(extensionServicesRaw);
          if (servicesArray && servicesArray.length > 0) {
            for (const service of servicesArray) {
              if (service.serviceName === 'TRANSFER_CERTIFICATE') {
                this.transferCertificateEnabled = service.serviceStatus === 'ENABLED';
              }
              if (service.serviceName === 'ID_CARD') {
                this.idCardServiceEnabled = service.serviceStatus === 'ENABLED';
              }
              if (service.serviceName === 'STAFF_ID_CARD') {
                this.staffIdCardServiceEnabled = service.serviceStatus === 'ENABLED';
              }
            }
          }
        } catch (e) {}
      }
    }
  }

  // ------------------------------------------------------------------
  // Bootstrap sequence - mirrors groups.component.ts isValidToken() flow:
  // getGroupsClassCategories() + getGroupsAcademicList().
  // ------------------------------------------------------------------
  private bootstrap(): void {
    this.getGroupsClassCategories();
    this.getGroupsAcademicList();
  }

  getGroupsClassCategories(): void {
    this.groupservice.groupsClassCategories(this.getSchoolId, this.getAuthToken).subscribe(
      (data: any) => {
        this.schoolCategories = data.schoolCategory || [];
      },
      () => {}
    );
  }

  getGroupsAcademicList(): void {
    this.groupservice.groupsStudentAcademicList(this.getSchoolId, this.getAuthToken).subscribe(
      (data: any) => {
        const list: AcademicYearItem[] = data.academicList || [];
        this.academicList = list;
        let indexOfCurrent = -1;
        for (let i = 0; i < list.length; i++) {
          list[i].current = false;
          list[i].notstarted = false;
          list[i].completed = false;
          if (list[i].InActive === 0) {
            indexOfCurrent = i;
            list[i].current = true;
            this.currentAcademicId = list[i].pkAcademicId;
            this.currentAcademicYearLabel = list[i].Year;
            this.currentAcademicState = 'RUNNING';
          } else if (list[i].InActive === 1) {
            const eDate = new Date((list[i] as any).EndDate);
            if (eDate < new Date()) {
              list[i].completed = true;
            } else {
              list[i].notstarted = true;
            }
          }
        }
        if (indexOfCurrent === -1 && list.length > 0) {
          this.currentAcademicId = list[0].pkAcademicId;
          this.currentAcademicYearLabel = list[0].Year;
          this.currentAcademicState = list[0].completed ? 'COMPLETED' : 'NOT_STARTED';
        }
        this.fetchStudents();
      },
      () => {
        this.loadingStudents = false;
      }
    );
  }

  academicChanged(newAcademicId: number): void {
    this.currentAcademicId = newAcademicId;
    for (const item of this.academicList) {
      if (item.pkAcademicId === newAcademicId) {
        this.currentAcademicYearLabel = item.Year;
        this.currentAcademicState = item.current ? 'RUNNING' : item.completed ? 'COMPLETED' : 'NOT_STARTED';
      }
    }
    this.pageIndex = 0;
    this.fetchStudents();
  }

  // ------------------------------------------------------------------
  // Filter change handlers - mirror selectcategoryFunction /
  // selectclassFunction / selectsectionFunction reset-of-dependents
  // behaviour from groups.component.ts.
  // ------------------------------------------------------------------
  onCategoryChange(categoryId: any): void {
    this.selectedCategory = categoryId;
    this.selectedClass = 0;
    this.selectedSection = 0;
    this.schoolClasses = [];
    this.schoolClassesSections = [];

    const catNum = Number(categoryId);
    if (catNum !== 0) {
      this.classDropdownDisabled = false;
      this.groupservice.groupsAvailableClass(this.getSchoolId, categoryId, this.getAuthToken).subscribe(
        (data: any) => {
          this.schoolClasses = data.className || [];
        },
        () => {}
      );
    } else {
      this.classDropdownDisabled = true;
      this.sectionDropdownDisabled = true;
    }

    this.pageIndex = 0;
    this.fetchStudents();
  }

  onClassChange(classId: any): void {
    this.selectedClass = classId;
    this.selectedSection = 0;
    this.schoolClassesSections = [];

    const classNum = Number(classId);
    if (classNum !== 0 || Number(this.selectedCategory) !== 0) {
      this.sectionDropdownDisabled = false;
      this.groupservice.groupsAvailableClassSection(classId, this.getAuthToken).subscribe(
        (data: any) => {
          this.schoolClassesSections = data || [];
        },
        () => {}
      );
    } else {
      this.sectionDropdownDisabled = true;
    }

    this.pageIndex = 0;
    this.fetchStudents();
  }

  onSectionChange(sectionId: any): void {
    this.selectedSection = sectionId;
    this.pageIndex = 0;
    this.fetchStudents();
  }

  get hasActiveStudentFilters(): boolean {
    return Number(this.selectedCategory) !== 0 || Number(this.selectedClass) !== 0 || Number(this.selectedSection) !== 0;
  }

  clearStudentFilters(): void {
    this.selectedCategory = 0;
    this.selectedClass = 0;
    this.selectedSection = 0;
    this.schoolClasses = [];
    this.schoolClassesSections = [];
    this.classDropdownDisabled = true;
    this.sectionDropdownDisabled = true;
    this.pageIndex = 0;
    this.fetchStudents();
  }

  // ------------------------------------------------------------------
  // Search (debounced) + Sort + Pagination
  // ------------------------------------------------------------------
  onSearchInput(value: string): void {
    this.searchInput$.next(value);
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.searchInput$.next('');
  }

  onSortClick(column: SortColumnKey): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.fetchStudents();
  }

  /* PrimeNG p-paginator emits {first, rows, page, pageCount} on every
     page-number click or rows-per-page dropdown change. */
  onPaginatorChange(event: { first?: number; rows?: number; page?: number }): void {
    this.pageSize = event.rows ?? this.pageSize;
    this.pageIndex = event.page ?? 0;
    this.fetchStudents();
  }

  // ------------------------------------------------------------------
  // Core fetch - POSTs to the exact same /studentlist endpoint with the
  // exact same body shape as groups.component.ts's initDataTableServerProcess
  // ajax.data() function:
  //   { schoolId, academicId, categoryId, classId, sectionId,
  //     countFrom, lengthOf, search, sort }
  // and the same headers (Content-Type: application/json, authToken).
  // ------------------------------------------------------------------
  fetchStudents(): void {
    if (this.currentAcademicId === null) {
      return;
    }
    this.loadingStudents = true;

    const order = this.sortColumn
      ? [{ column: SORT_COLUMN_INDEX[this.sortColumn], dir: this.sortDirection }]
      : [];

    const body = {
      schoolId: localStorage.getItem('schoolinfo'),
      academicId: this.currentAcademicId,
      categoryId: this.selectedCategory,
      classId: this.selectedClass || 'null',
      sectionId: this.selectedSection || 'null',
      countFrom: this.pageIndex * this.pageSize,
      lengthOf: this.pageSize,
      search: this.searchTerm,
      sort: order,
    };

    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('authToken', this.getAuthToken);

    this.http.post(AppSettings.API_ENDPOINT + '/studentlist', body, { headers }).subscribe(
      (response: any) => {
        this.students = response?.data || [];
        this.totalRecords = response?.recordsFiltered ?? response?.recordsTotal ?? this.students.length;
        this.loadingStudents = false;
      },
      () => {
        this.students = [];
        this.totalRecords = 0;
        this.loadingStudents = false;
      }
    );
  }

  // ------------------------------------------------------------------
  // Row action menu (pencil icon dropdown)
  // ------------------------------------------------------------------
  // Positioned in fixed/viewport coordinates (not CSS position:absolute
  // relative to the row) so it always renders on top of .gv2-table-wrap's
  // content instead of being clipped by that container's overflow-x:auto
  // (which per the CSS spec also forces overflow-y clipping - z-index alone
  // can't override that, only escaping the clipped ancestor's box can).
  // When flipUp is true, `top` is the icon's top edge and the menu grows
  // upward from there via CSS transform instead of downward from `top`.
  actionMenuPosition: { top: number; left: number; flipUp: boolean } | null = null;

  // Its position is computed once at open time (see toggleActionMenu), so
  // it doesn't track the row as the page scrolls - close it on any scroll
  // instead of letting it stay pinned in place while the row moves under it.
  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (this.openActionMenuForRowId !== null) {
      this.openActionMenuForRowId = null;
      this.actionMenuPosition = null;
    }
    if (this.exportMenuOpen) {
      this.exportMenuOpen = false;
      this.exportMenuPosition = null;
    }
  }

  toggleActionMenu(row: StudentRow, event?: MouseEvent): void {
    const rowId = row.DT_RowId ?? null;
    if (this.openActionMenuForRowId === rowId) {
      this.openActionMenuForRowId = null;
      this.actionMenuPosition = null;
      return;
    }
    const target = event?.currentTarget as HTMLElement | undefined;
    if (target) {
      const rect = target.getBoundingClientRect();
      const estimatedMenuHeight = 220;
      const flipUp = window.innerHeight - rect.bottom < estimatedMenuHeight;
      this.actionMenuPosition = {
        top: flipUp ? rect.top - 4 : rect.bottom + 4,
        left: rect.left,
        flipUp,
      };
    } else {
      this.actionMenuPosition = null;
    }
    this.openActionMenuForRowId = rowId;
  }

  closeActionMenu(): void {
    this.openActionMenuForRowId = null;
    this.actionMenuPosition = null;
    this.columnDropdownOpen = false;
    this.addStudentMenuOpen = false;
    this.addStaffMenuOpen = false;
    this.exportMenuOpen = false;
    this.exportMenuPosition = null;
    this.sortRollNumberMenuOpen = false;
  }

  addStudentMenuOpen = false;

  toggleAddStudentMenu(): void {
    this.addStudentMenuOpen = !this.addStudentMenuOpen;
  }

  // position: fixed + JS-computed coordinates (like the row action menu -
  // see toggleActionMenu) since .gv2-card has overflow:hidden, which would
  // otherwise clip a CSS position:absolute dropdown at the card's edge.
  exportMenuOpen = false;
  exportMenuPosition: { top: number; left: number } | null = null;

  toggleExportMenu(event?: MouseEvent): void {
    if (this.exportMenuOpen) {
      this.exportMenuOpen = false;
      this.exportMenuPosition = null;
      return;
    }
    const target = event?.currentTarget as HTMLElement | undefined;
    if (target) {
      const rect = target.getBoundingClientRect();
      this.exportMenuPosition = { top: rect.bottom + 4, left: rect.left };
    }
    this.exportMenuOpen = true;
  }

  // ------------------------------------------------------------------
  // Update Basic Info - native groups-v2 popup that replaces the shared
  // <app-student-basic-info> component (still used unmodified by the OLD
  // groups.component). Reuses StudentBasicInfoService's exact endpoints/
  // request shapes so backend behavior is unchanged.
  //
  // Phone number: matches admission-v2.component.ts exactly by reusing
  // jQuery's intlTelInput plugin (already loaded globally via CDN in
  // index.html) for the country-code-aware phone input — see
  // initBasicInfoPhoneInput() below. This is the one deliberate exception to
  // groups-v2 otherwise staying jQuery-free. The underlying data fields
  // (basicInfoCountryCode / basicInfoSelectedContact) and validation
  // (libphonenumber-js) are unchanged from the shared component.
  // ------------------------------------------------------------------
  basicInfoLoading = false;
  basicInfoSaving = false;
  basicInfoData: any = {};
  basicInfoRegistrationId: any = '';
  basicInfoHouseNameList: any[] = [];
  basicInfoCategoryList: any[] = [];
  basicInfoClassStandardsList: any[] = [];
  basicInfoSectionList: any[] = [];
  basicInfoInitialClassId: number;
  basicInfoInitialClassStandardId: number;
  basicInfoInitialClassCategoryId: number;
  basicInfoInitialCategoryList: any[] = [];
  basicInfoInitialClassStandardsList: any[] = [];
  basicInfoInitialSectionList: any[] = [];
  basicInfoAdmittedClassYear: any = '';
  basicInfoShowClassChangeWarning = false;
  basicInfoClassChangeError: any = {};
  basicInfoRemovingPic = false;
  basicInfoInvalidMobileNumber = false;
  basicInfoSelectedContact = '';
  basicInfoCountryCode = this.brand.defaultCountryDialCode || '91';
  basicInfoCasteCertFileType = '';
  basicInfoSpeciallyAbledFileType = '';
  basicInfoEwsCertFileType = '';
  basicInfoSingleGirlCertFileType = '';
  basicInfoTcOrBirthCertFileType = '';
  basicInfoAadharFileType = '';

  get basicInfoSkeletonRows(): number[] {
    return this.basicInfoLoading ? Array.from({ length: 8 }) : [];
  }

  openBasicInfo(row: StudentRow): void {
    this.studentBasicInfoStudentId = row.DT_RowId;
    this.studentBasicInfoStudentName = row.studentName;
    this.basicInfoRegistrationId = row.registrationId;
    this.openStudentBasicInfoPopup = true;
    this.closeActionMenu();
    this.resetBasicInfoState();
    this.getBasicInfoHouseList();
    this.getBasicInfoDetails();
  }

  private resetBasicInfoState(): void {
    this.basicInfoData = {};
    this.basicInfoCategoryList = [];
    this.basicInfoClassStandardsList = [];
    this.basicInfoSectionList = [];
    this.basicInfoShowClassChangeWarning = false;
    this.basicInfoClassChangeError = {};
    this.basicInfoInvalidMobileNumber = false;
    this.basicInfoSelectedContact = '';
    this.basicInfoCountryCode = this.brand.defaultCountryDialCode || '91';
    this.basicInfoCasteCertFileType = '';
    this.basicInfoSpeciallyAbledFileType = '';
    this.basicInfoEwsCertFileType = '';
    this.basicInfoSingleGirlCertFileType = '';
    this.basicInfoTcOrBirthCertFileType = '';
    this.basicInfoAadharFileType = '';
  }

  getBasicInfoHouseList(): void {
    this.studentBasicInfoService.getSchoolHouseList(localStorage.getItem('usertoken')).subscribe(
      (data: any) => {
        this.basicInfoHouseNameList = data.status ? JSON.parse(JSON.stringify(data.list)) : [];
      },
      () => {
        this.basicInfoHouseNameList = [];
      }
    );
  }

  private basicInfoFormatDate(date: any): string {
    if (!date) {
      return '-';
    }
    /* getStudentDetailsForEditForm can return an already human-formatted
       string (e.g. "Sep 07 , 2017") rather than ISO — moment_js can't parse
       that reliably (throws the RFC2822/ISO deprecation warning and can
       break the enclosing subscribe callback). Parse with the native Date
       first and only fall back to moment_js for genuinely parseable input. */
    const nativeParsed = new Date(date);
    if (!isNaN(nativeParsed.getTime())) {
      const day = String(nativeParsed.getDate()).padStart(2, '0');
      const month = String(nativeParsed.getMonth() + 1).padStart(2, '0');
      return `${day}/${month}/${nativeParsed.getFullYear()}`;
    }
    try {
      return moment_js(date).format('DD/MM/YYYY');
    } catch {
      return String(date);
    }
  }

  getBasicInfoDetails(): void {
    this.basicInfoLoading = true;
    this.studentBasicInfoService.getStudentDetailsForEditForm(this.studentBasicInfoStudentId, localStorage.getItem('usertoken')).subscribe(
      (data: any) => {
        this.basicInfoLoading = false;
        this.basicInfoData = JSON.parse(JSON.stringify(data.student));
        /* Backend can return this as a number or string — the <select>'s
           [value] binding needs a plain string to match its <option>s. */
        this.basicInfoCountryCode = String(this.basicInfoData.studentCountryCode || this.brand.defaultCountryDialCode || '91');
        this.basicInfoSelectedContact = this.basicInfoData.studentPhone;

        this.basicInfoCategoryList = data.categories;
        this.basicInfoClassStandardsList = data.classStandards;
        this.basicInfoSectionList = data.sections;
        this.basicInfoInitialCategoryList = data.categories;
        this.basicInfoInitialClassStandardsList = data.classStandards;
        this.basicInfoInitialSectionList = data.sections;
        this.basicInfoInitialClassId = data.student.classId;
        this.basicInfoInitialClassStandardId = data.student.fkClassStandardsId;
        this.basicInfoInitialClassCategoryId = data.student.categoryId;
        this.basicInfoAdmittedClassYear = this.basicInfoData.admittedClass + ', ' + this.basicInfoData.admittedAcademic;

        if (this.basicInfoData.casteCertificate) {
          this.basicInfoCasteCertFileType = this.commonFunction.getFileTypeFromUrl(this.basicInfoData.casteCertificate);
        }
        if (this.basicInfoData.aadharCopy) {
          this.basicInfoAadharFileType = this.commonFunction.getFileTypeFromUrl(this.basicInfoData.aadharCopy);
        }
        if (this.basicInfoData.tcOrBirthCertificate) {
          this.basicInfoTcOrBirthCertFileType = this.commonFunction.getFileTypeFromUrl(this.basicInfoData.tcOrBirthCertificate);
        }
        if (this.basicInfoData.ewsCertificate) {
          this.basicInfoEwsCertFileType = this.commonFunction.getFileTypeFromUrl(this.basicInfoData.ewsCertificate);
        }
        if (this.basicInfoData.speciallyAbledCertificate) {
          this.basicInfoSpeciallyAbledFileType = this.commonFunction.getFileTypeFromUrl(this.basicInfoData.speciallyAbledCertificate);
        }
        if (this.basicInfoData.singleGirlChildCertificate) {
          this.basicInfoSingleGirlCertFileType = this.commonFunction.getFileTypeFromUrl(this.basicInfoData.singleGirlChildCertificate);
        }

        this.basicInfoData.isMinority = this.basicInfoData.isMinority == 1 ? true : false;
        if (this.basicInfoData.dateOfBirth) {
          this.basicInfoData.dateOfBirth = this.basicInfoFormatDate(this.basicInfoData.dateOfBirth);
        }
        if (this.basicInfoData.dateOfAdmission) {
          this.basicInfoData.dateOfAdmission = this.basicInfoFormatDate(this.basicInfoData.dateOfAdmission);
        }
        this.basicInfoData.tcOrBirthCertificateUploaded = data.student.tcOrBirthCertificateUploaded == 'NO' ? 0 : 1;

        // Wait for Angular to render the now-populated form (the phone
        // input only exists in the DOM once basicInfoLoading is false and
        // *ngIf reveals the form) before handing it to intlTelInput.
        setTimeout(() => this.initBasicInfoPhoneInput(), 0);
      },
      () => {
        this.basicInfoLoading = false;
      }
    );
  }

  /* Mirrors admission-v2.component.ts's mobileNumbersValidation() for the
     single student-contact field: same plugin options, same country-match-
     by-dial-code lookup, same countrychange handler shape. */
  private initBasicInfoPhoneInput(): void {
    const input = $('#gv2-basicinfo-phone-input');
    if (!input.length || typeof input.intlTelInput !== 'function') {
      return;
    }
    input.intlTelInput('destroy');

    const countryData = (window as any).intlTelInputGlobals?.getCountryData?.() || [];
    const matchedCountry = countryData.find((country: any) => `${country.dialCode}` === `${this.basicInfoCountryCode}`);

    input.intlTelInput({
      initialCountry: matchedCountry ? matchedCountry.iso2 : 'in',
      separateDialCode: false,
      autoPlaceholder: 'off',
      autoFormat: false,
      nationalMode: false,
      dropdownContainer: document.body,
      formatOnDisplay: false,
      utilsScript: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/11.0.4/js/utils.js',
    });

    input.off('countrychange').on('countrychange', () => {
      input.val('');
      this.basicInfoSelectedContact = '';
      this.basicInfoInvalidMobileNumber = false;
      const selected = input.intlTelInput('getSelectedCountryData');
      this.basicInfoCountryCode = selected.dialCode;
    });
  }

  private basicInfoValidateSelectedFile(file: File): boolean {
    const extension = (file.name.split('.').pop() || '').toLowerCase();
    const allowed = ['jpeg', 'png', 'jpg', 'pdf'];
    if (allowed.indexOf(extension) === -1) {
      customToastr('Please select a valid image type of jpeg, png, jpg or pdf', 'error');
      return false;
    }
    if (file.size > (1024 * 1024 * 20)) {
      customToastr('Image size should not exceed 20 MB', 'error');
      return false;
    }
    return true;
  }

  onBasicInfoProfilePicSelected(event: any): void {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      if (!this.basicInfoValidateSelectedFile(file)) {
        event.target.value = '';
        return;
      }
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.basicInfoData.studentImage = e.target.result;
      };
      reader.readAsDataURL(file);

      this.basicInfoLoading = true;
      this.studentBasicInfoService.updateStudentInfo(localStorage.getItem('usertoken'), this.studentBasicInfoStudentId, file, 'profilePic').subscribe(
        (data: any) => {
          this.basicInfoLoading = false;
          if (data.processedImageUrl) {
            this.basicInfoData.studentImage = data.processedImageUrl[0];
            customToastr('Student profile image successfully updated', 'success');
          } else {
            customToastr('Failed to update student profile image, please try again', 'error');
          }
        },
        () => {
          this.basicInfoLoading = false;
          customToastr('Failed to update student profile image, please try again', 'error');
        }
      );
      event.target.value = '';
    }
  }

  removeBasicInfoProfilePic(): void {
    this.basicInfoRemovingPic = true;
    this.studentBasicInfoService.removeStudentProfilePic(localStorage.getItem('usertoken'), this.studentBasicInfoStudentId).subscribe(
      (data: any) => {
        this.basicInfoRemovingPic = false;
        if (data.errorcode) {
          customToastr('Failed to delete student image, please try again', 'error');
        } else {
          this.basicInfoData.studentImage = '';
          customToastr('Student profile image successfully deleted', 'success');
        }
      },
      () => {
        this.basicInfoRemovingPic = false;
        customToastr('Failed to delete student image, please try again', 'error');
      }
    );
  }

  private basicInfoUploadDocument(event: any, imageFor: string, fileTypeSetter: (type: string) => void, dataKey: string, successMessage: string, errorMessage: string): void {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      fileTypeSetter(file.type);
      if (!this.basicInfoValidateSelectedFile(file)) {
        event.target.value = '';
        return;
      }
      this.basicInfoLoading = true;
      this.studentBasicInfoService.updateStudentInfo(localStorage.getItem('usertoken'), this.studentBasicInfoStudentId, file, imageFor).subscribe(
        (data: any) => {
          this.basicInfoLoading = false;
          if (data.processedImageUrl) {
            this.basicInfoData[dataKey] = data.processedImageUrl[0];
          }
          customToastr(successMessage, 'success');
        },
        () => {
          this.basicInfoLoading = false;
          customToastr(errorMessage, 'error');
        }
      );
      event.target.value = '';
    }
  }

  onBasicInfoAadharCopySelected(event: any): void {
    if (!this.basicInfoData.aadharNumber || this.basicInfoData.aadharNumber.trim() === '') {
      customToastr('Please enter aadhar number before uploading aadhar pic', 'error');
      event.target.value = '';
      return;
    }
    this.basicInfoUploadDocument(
      event, 'aadharReference',
      (t) => this.basicInfoAadharFileType = t,
      'aadharCopy',
      'Student aadhar copy successfully updated',
      'Failed to update student aadhar copy, please try again'
    );
  }

  onBasicInfoTcBirthCertSelected(event: any): void {
    this.basicInfoUploadDocument(
      event, 'tcOrBirthCertificate',
      (t) => this.basicInfoTcOrBirthCertFileType = t,
      'tcOrBirthCertificate',
      'Student TC/Birth certificate successfully updated',
      'Failed to update student TC/Birth certificate, please try again'
    );
  }

  onBasicInfoCasteCertSelected(event: any): void {
    this.basicInfoUploadDocument(
      event, 'casteCertificate',
      (t) => this.basicInfoCasteCertFileType = t,
      'casteCertificate',
      'Student caste certificate successfully updated',
      'Failed to update student caste certificate, please try again'
    );
  }

  onBasicInfoSpeciallyAbledCertSelected(event: any): void {
    this.basicInfoUploadDocument(
      event, 'speciallyAbled',
      (t) => this.basicInfoSpeciallyAbledFileType = t,
      'speciallyAbledCertificate',
      'Student specially-abled certificate successfully updated',
      'Failed to update student specially-abled certificate, please try again'
    );
  }

  onBasicInfoEwsCertSelected(event: any): void {
    this.basicInfoUploadDocument(
      event, 'ewsCertificate',
      (t) => this.basicInfoEwsCertFileType = t,
      'ewsCertificate',
      'Student EWS certificate successfully updated',
      'Failed to update student EWS certificate, please try again'
    );
  }

  onBasicInfoSingleGirlCertSelected(event: any): void {
    this.basicInfoUploadDocument(
      event, 'singleGirlChildCertificate',
      (t) => this.basicInfoSingleGirlCertFileType = t,
      'singleGirlChildCertificate',
      'Student single girl child certificate successfully updated',
      'Failed to update student single girl child certificate, please try again'
    );
  }

  onBasicInfoCategoryChange(): void {
    this.basicInfoLoading = true;
    this.studentBasicInfoService.getClassStandardsByCategory(this.basicInfoData.categoryId, localStorage.getItem('usertoken')).subscribe(
      (data: any) => {
        this.basicInfoLoading = false;
        this.basicInfoClassStandardsList = data;
        this.basicInfoData.fkClassStandardsId = null;
        this.basicInfoData.classId = null;
        this.basicInfoSectionList = [];
      },
      () => {
        this.basicInfoLoading = false;
        customToastr('Failed to fetch class standards, please try again', 'error');
        this.basicInfoClassStandardsList = [];
        this.basicInfoData.fkClassStandardsId = null;
        this.basicInfoData.classId = null;
        this.basicInfoSectionList = [];
      }
    );
  }

  onBasicInfoClassChange(): void {
    this.basicInfoLoading = true;
    this.studentBasicInfoService.getClassSectionsByClassIdFun(this.basicInfoData.fkClassStandardsId, localStorage.getItem('usertoken')).subscribe(
      (data: any) => {
        this.basicInfoLoading = false;
        this.basicInfoSectionList = data;
        this.basicInfoData.classId = null;
      },
      () => {
        this.basicInfoLoading = false;
        customToastr('Failed to fetch class sections, please try again', 'error');
        this.basicInfoData.classId = null;
        this.basicInfoSectionList = [];
      }
    );
  }

  onBasicInfoSectionChange(): void {
    if (this.basicInfoData.classId !== this.basicInfoInitialClassId) {
      this.basicInfoLoading = true;
      this.studentBasicInfoService.studentClassChangeValidation(this.basicInfoData.classId, this.basicInfoInitialClassId, this.studentBasicInfoStudentId, localStorage.getItem('usertoken')).subscribe(
        (data: any) => {
          this.basicInfoLoading = false;
          if (data.allowUpdate == 'NO') {
            this.basicInfoShowClassChangeWarning = true;
            this.basicInfoClassChangeError = data;
          }
        },
        () => {
          this.basicInfoLoading = false;
          this.basicInfoShowClassChangeWarning = false;
          this.basicInfoClassChangeError = {};
          customToastr('Unable to complete requested action, please try again', 'error');
        }
      );
    }
  }

  closeBasicInfoFeesNotification(): void {
    this.basicInfoShowClassChangeWarning = false;
    this.basicInfoCategoryList = this.basicInfoInitialCategoryList;
    this.basicInfoClassStandardsList = this.basicInfoInitialClassStandardsList;
    this.basicInfoSectionList = this.basicInfoInitialSectionList;
    this.basicInfoData.classId = this.basicInfoInitialClassId;
    this.basicInfoData.fkClassStandardsId = this.basicInfoInitialClassStandardId;
    this.basicInfoData.categoryId = this.basicInfoInitialClassCategoryId;
  }

  onBasicInfoGenderChange(): void {
    this.basicInfoData.isSingleGirlChild = (this.basicInfoData.gender === 1) ? 0 : this.basicInfoData.isSingleGirlChild;
  }

  onBasicInfoCasteChange(): void {
    this.basicInfoData.isEWSCategory = (this.basicInfoData.cast !== 'General') ? 0 : this.basicInfoData.isEWSCategory;
  }

  onBasicInfoMinorityChange(event: any): void {
    this.basicInfoData.isMinority = event.target.checked === true ? 1 : 0;
  }

  onBasicInfoSpeciallyAbledChange(event: any): void {
    this.basicInfoData.isSpeciallyAbled = event.target.checked === true ? 1 : 0;
  }

  onBasicInfoEWSCategoryChange(event: any): void {
    this.basicInfoData.isEWSCategory = event.target.checked === true ? 1 : 0;
  }

  onBasicInfoSingleGirlChildChange(event: any): void {
    this.basicInfoData.isSingleGirlChild = event.target.checked === true ? 1 : 0;
  }

  onBasicInfoContactInput(value: string): void {
    this.basicInfoSelectedContact = value;
    // Basic-shape validation only (no intlTelInput here — see class comment
    // above openBasicInfo for why): flags a number as invalid if it is
    // non-numeric or not between 6-15 digits, roughly matching the
    // libphonenumber-js validity check the shared component performs.
    const digitsOnly = /^[0-9]{6,15}$/;
    this.basicInfoInvalidMobileNumber = !!value && !digitsOnly.test(value);
  }

  async saveBasicInfo(): Promise<void> {
    if (this.basicInfoSelectedContact && this.basicInfoInvalidMobileNumber) {
      customToastr('Please enter valid student mobile number', 'error');
      return;
    }

    const studentDOBraw = this.basicInfoData.dateOfBirth;
    const admissionDateRaw = this.basicInfoData.dateOfAdmission;
    if (!studentDOBraw || studentDOBraw === '') {
      customToastr('Please select student date of birth', 'error');
      return;
    }
    if (!admissionDateRaw || admissionDateRaw === '') {
      customToastr('Please select date of admission', 'error');
      return;
    }

    if (this.basicInfoData.penNumber) {
      const penNumberLength = this.basicInfoData.penNumber.length;
      if (penNumberLength < 11 || penNumberLength > 12) {
        customToastr('Please enter a valid PEN number with 11 or 12 digits', 'error');
        return;
      }
    }

    if (this.basicInfoData.pinCode && this.basicInfoData.pinCode.length < 6) {
      customToastr('Please enter valid pin code, it is exactly 6 digits', 'error');
      return;
    }

    const studentDOBIsValid = this.formValidator.valdateDOB(studentDOBraw);
    if (studentDOBIsValid) {
      customToastr(studentDOBIsValid, 'error');
      return;
    }

    const dobParts = studentDOBraw.split('/');
    const formattedDOB = `${dobParts[2]}-${dobParts[1]}-${dobParts[0]}`;
    const admissionParts = admissionDateRaw.split('/');
    const formattedAdmissionDate = `${admissionParts[2]}-${admissionParts[1]}-${admissionParts[0]}`;

    const reqObj: any = {
      aadharNumber: this.basicInfoData.aadharNumber,
      classId: this.basicInfoData.classId,
      countryCode: this.basicInfoData.countryCode,
      dateOfBirth: formattedDOB,
      dateOfAdmission: formattedAdmissionDate,
      gender: this.basicInfoData.gender != null ? this.basicInfoData.gender.toString() : this.basicInfoData.gender,
      houseName: this.basicInfoData.houseName,
      parentName: this.basicInfoData.parentName,
      registrationId: this.basicInfoData.registrationId,
      rollNumber: this.basicInfoData.rollNumber,
      schoolId: this.basicInfoData.schoolId,
      studentEmail: this.basicInfoData.studentEmail,
      studentId: this.basicInfoData.studentId,
      studentName: this.basicInfoData.studentName,
      studentCountryCode: this.basicInfoCountryCode,
      studentPhone: this.basicInfoSelectedContact,
      surname: this.basicInfoData.surname,
      tcOrBirthCertificateUploaded: this.basicInfoData.tcOrBirthCertificateUploaded == 1 ? 'YES' : 'NO',
      academicId: this.currentAcademicId,
      penNumber: this.basicInfoData.penNumber,
      caste: this.basicInfoData.cast,
      isMinority: this.basicInfoData.isMinority,
      isSpeciallyAbled: this.basicInfoData.isSpeciallyAbled,
      isEWSCategory: this.basicInfoData.isEWSCategory,
      isSingleGirlChild: this.basicInfoData.isSingleGirlChild,
    };

    const studentValidateName = this.formValidator.validateFieldName(this.basicInfoData.studentName, 'Student ');
    const parentValidateName = this.formValidator.validateFieldName(this.basicInfoData.parentName, 'Parent ');

    if (this.basicInfoData.studentPhone) {
      const mobileNumberIsvalid = this.formValidator.validateMobileNumberForNewAdmission('Student', this.basicInfoData.studentPhone);
      if (mobileNumberIsvalid) {
        customToastr(mobileNumberIsvalid, 'error');
        return;
      }
    }
    if (this.basicInfoData.aadharNumber) {
      const aadharNumberIsvalid = this.formValidator.validateAadharNumber(this.basicInfoData.aadharNumber);
      if (aadharNumberIsvalid) {
        customToastr(aadharNumberIsvalid, 'error');
        return;
      }
    }
    if (this.basicInfoData.studentEmail) {
      const studentEmailIsvalid = this.formValidator.validateIsEmail(this.basicInfoData.studentEmail);
      if (!studentEmailIsvalid) {
        customToastr('Please enter valid email address', 'error');
        return;
      }
    }

    const rollNumberIsNumeric = reqObj.rollNumber !== null && reqObj.rollNumber !== undefined && reqObj.rollNumber !== '' && !isNaN(Number(reqObj.rollNumber));

    if (
      (reqObj.countryCode && reqObj.classId) &&
      (reqObj.gender === '0' || reqObj.gender === '1') &&
      (reqObj.tcOrBirthCertificateUploaded === 'YES' || reqObj.tcOrBirthCertificateUploaded === 'NO') &&
      !parentValidateName &&
      (reqObj.registrationId && reqObj.schoolId && reqObj.studentId) &&
      !studentValidateName &&
      rollNumberIsNumeric
    ) {
      this.basicInfoSaving = true;
      this.studentBasicInfoService.updateStudentDetails(reqObj, localStorage.getItem('usertoken')).subscribe(
        (data: any) => {
          this.basicInfoSaving = false;
          if (data.status) {
            customToastr('Student details successfully updated', 'success');
            this.afterStudentBasicInfoSaved();
          } else if (data.message) {
            customToastr(data.message, 'error');
          }
        },
        () => {
          this.basicInfoSaving = false;
          customToastr('Unable to complete student profile update, please try again', 'error');
        }
      );
    } else {
      if (studentValidateName) {
        customToastr(studentValidateName, 'error');
      } else if (parentValidateName) {
        customToastr(parentValidateName, 'error');
      } else {
        customToastr('Please fill all mandatory details', 'error');
      }
    }
  }

  afterStudentBasicInfoSaved(): void {
    this.openStudentBasicInfoPopup = false;
    this.fetchStudents();
  }

  studentBasicInfoPopupClose(): void {
    this.openStudentBasicInfoPopup = false;
    const input = $('#gv2-basicinfo-phone-input');
    if (input.length && typeof input.intlTelInput === 'function') {
      input.intlTelInput('destroy');
    }
  }

  openContactDetails(row: StudentRow): void {
    this.studentContactInfoStudentId = row.DT_RowId;
    this.studentContactInfoStudentName = row.studentName;
    this.openStudentContactInfoPopup = true;
    this.closeActionMenu();
    this.resetContactInfoState();
    this.getContactInfoDetails();
  }

  private resetContactInfoState(): void {
    this.contactInfoData = {};
    this.contactInfoFatherContact = '';
    this.contactInfoMotherContact = '';
    this.contactInfoGuardianContact = '';
    this.contactInfoFatherCountryCode = this.brand.defaultCountryDialCode || '91';
    this.contactInfoMotherCountryCode = this.brand.defaultCountryDialCode || '91';
    this.contactInfoGuardianCountryCode = this.brand.defaultCountryDialCode || '91';
    this.contactInfoInvalidFatherContact = false;
    this.contactInfoInvalidMotherContact = false;
    this.contactInfoInvalidGuardianContact = false;
    this.contactInfoFatherCountry = undefined;
    this.contactInfoMotherCountry = undefined;
    this.contactInfoGuardianCountry = undefined;

    this.contactInfoFatherAltContact = '';
    this.contactInfoFatherAltCountryCode = this.brand.defaultCountryDialCode || '91';
    this.contactInfoInvalidFatherAltContact = false;
    this.contactInfoFatherAltCountry = undefined;
    this.contactInfoFatherWhatsapp = '';
    this.contactInfoFatherWhatsappCountryCode = this.brand.defaultCountryDialCode || '91';
    this.contactInfoInvalidFatherWhatsapp = false;
    this.contactInfoFatherWhatsappCountry = undefined;

    this.contactInfoMotherAltContact = '';
    this.contactInfoMotherAltCountryCode = this.brand.defaultCountryDialCode || '91';
    this.contactInfoInvalidMotherAltContact = false;
    this.contactInfoMotherAltCountry = undefined;
    this.contactInfoMotherWhatsapp = '';
    this.contactInfoMotherWhatsappCountryCode = this.brand.defaultCountryDialCode || '91';
    this.contactInfoInvalidMotherWhatsapp = false;
    this.contactInfoMotherWhatsappCountry = undefined;

    this.contactInfoGuardianAltContact = '';
    this.contactInfoGuardianAltCountryCode = this.brand.defaultCountryDialCode || '91';
    this.contactInfoInvalidGuardianAltContact = false;
    this.contactInfoGuardianAltCountry = undefined;
    this.contactInfoGuardianWhatsapp = '';
    this.contactInfoGuardianWhatsappCountryCode = this.brand.defaultCountryDialCode || '91';
    this.contactInfoInvalidGuardianWhatsapp = false;
    this.contactInfoGuardianWhatsappCountry = undefined;

    this.contactInfoFatherImagePreview = null;
    this.contactInfoMotherImagePreview = null;
    this.contactInfoGuardianImagePreview = null;
    this.contactInfoFatherImageFile = null;
    this.contactInfoMotherImageFile = null;
    this.contactInfoGuardianImageFile = null;
    this.contactInfoPendingImageDeletions.clear();
  }

  getContactInfoDetails(): void {
    this.contactInfoLoading = true;
    this.studentContactInfoService.getStudentContactDetailsForEditForm(this.studentContactInfoStudentId, localStorage.getItem('usertoken')).subscribe(
      (data: any) => {
        this.contactInfoLoading = false;
        this.contactInfoData = JSON.parse(JSON.stringify(data.student));
        this.contactInfoFatherImagePreview = this.contactInfoData.fatherImage || null;
        this.contactInfoMotherImagePreview = this.contactInfoData.motherImage || null;
        this.contactInfoGuardianImagePreview = this.contactInfoData.guardianImage || null;
        this.contactInfoFatherContact = this.contactInfoData.mobileNumber || '';
        this.contactInfoMotherContact = this.contactInfoData.motherPhone || '';
        this.contactInfoGuardianContact = this.contactInfoData.guardianPhone || '';
        this.contactInfoFatherCountryCode = String(this.contactInfoData.countryCode || this.brand.defaultCountryDialCode || '91');
        this.contactInfoMotherCountryCode = String(this.contactInfoData.motherCountryCode || this.brand.defaultCountryDialCode || '91');
        this.contactInfoGuardianCountryCode = String(this.contactInfoData.guardianCountryCode || this.brand.defaultCountryDialCode || '91');
        this.contactInfoFatherCountry = this.findCountryByDialCode(this.contactInfoFatherCountryCode);
        this.contactInfoMotherCountry = this.findCountryByDialCode(this.contactInfoMotherCountryCode);
        this.contactInfoGuardianCountry = this.findCountryByDialCode(this.contactInfoGuardianCountryCode);

        this.contactInfoFatherAltContact = this.contactInfoData.fatherAlternateNumber || '';
        this.contactInfoFatherAltCountryCode = String(this.contactInfoData.fatherAlternateCountryCode || this.brand.defaultCountryDialCode || '91');
        this.contactInfoFatherAltCountry = this.findCountryByDialCode(this.contactInfoFatherAltCountryCode);
        this.contactInfoFatherWhatsapp = this.contactInfoData.fatherWhatsappNumber || '';
        this.contactInfoFatherWhatsappCountryCode = String(this.contactInfoData.fatherWhatsappCountryCode || this.brand.defaultCountryDialCode || '91');
        this.contactInfoFatherWhatsappCountry = this.findCountryByDialCode(this.contactInfoFatherWhatsappCountryCode);

        this.contactInfoMotherAltContact = this.contactInfoData.motherAlternateNumber || '';
        this.contactInfoMotherAltCountryCode = String(this.contactInfoData.motherAlternateCountryCode || this.brand.defaultCountryDialCode || '91');
        this.contactInfoMotherAltCountry = this.findCountryByDialCode(this.contactInfoMotherAltCountryCode);
        this.contactInfoMotherWhatsapp = this.contactInfoData.motherWhatsappNumber || '';
        this.contactInfoMotherWhatsappCountryCode = String(this.contactInfoData.motherWhatsappCountryCode || this.brand.defaultCountryDialCode || '91');
        this.contactInfoMotherWhatsappCountry = this.findCountryByDialCode(this.contactInfoMotherWhatsappCountryCode);

        this.contactInfoGuardianAltContact = this.contactInfoData.guardianAlternateNumber || '';
        this.contactInfoGuardianAltCountryCode = String(this.contactInfoData.guardianAlternateCountryCode || this.brand.defaultCountryDialCode || '91');
        this.contactInfoGuardianAltCountry = this.findCountryByDialCode(this.contactInfoGuardianAltCountryCode);
        this.contactInfoGuardianWhatsapp = this.contactInfoData.guardianWhatsappNumber || '';
        this.contactInfoGuardianWhatsappCountryCode = String(this.contactInfoData.guardianWhatsappCountryCode || this.brand.defaultCountryDialCode || '91');
        this.contactInfoGuardianWhatsappCountry = this.findCountryByDialCode(this.contactInfoGuardianWhatsappCountryCode);
      },
      () => {
        this.contactInfoLoading = false;
      }
    );
  }

  /* Contact Details popup only: uses an-country-code-selector's
     CountryCodeService instead of jQuery's intlTelInput - the rest of
     groups-v2 (Basic Info/Add/Edit Staff) still uses intlTelInput and is
     intentionally left untouched. */
  private findCountryByDialCode(dialCode: string): Country | undefined {
    const normalized = '+' + String(dialCode || '').replace(/^\+/, '');
    const matches = this.countryCodeService.getByDialCode(normalized);
    return matches[0] || this.countryCodeService.getByIso2(this.brand.defaultCountryIso2Upper || 'IN');
  }

  private toDialCodeString(country: Country | undefined): string {
    return country?.dialCode ? country.dialCode.replace(/^\+/, '') : (this.brand.defaultCountryDialCode || '91');
  }

  private isValidContactNumber(value: string, country: Country | undefined): boolean {
    if (!value) {
      return true;
    }
    if (!/^[0-9]+$/.test(value)) {
      return false;
    }
    return country ? this.countryCodeService.isValidLength(country, value) : value.length >= 6 && value.length <= 15;
  }

  onContactInfoFatherContactInput(value: string): void {
    this.contactInfoFatherContact = value;
    this.contactInfoInvalidFatherContact = !this.isValidContactNumber(value, this.contactInfoFatherCountry);
  }

  onContactInfoMotherContactInput(value: string): void {
    this.contactInfoMotherContact = value;
    this.contactInfoInvalidMotherContact = !this.isValidContactNumber(value, this.contactInfoMotherCountry);
  }

  onContactInfoGuardianContactInput(value: string): void {
    this.contactInfoGuardianContact = value;
    this.contactInfoInvalidGuardianContact = !this.isValidContactNumber(value, this.contactInfoGuardianCountry);
  }

  onContactInfoFatherAltContactInput(value: string): void {
    this.contactInfoFatherAltContact = value;
    this.contactInfoInvalidFatherAltContact = !this.isValidContactNumber(value, this.contactInfoFatherAltCountry);
  }

  onContactInfoFatherWhatsappInput(value: string): void {
    this.contactInfoFatherWhatsapp = value;
    this.contactInfoInvalidFatherWhatsapp = !this.isValidContactNumber(value, this.contactInfoFatherWhatsappCountry);
  }

  onContactInfoMotherAltContactInput(value: string): void {
    this.contactInfoMotherAltContact = value;
    this.contactInfoInvalidMotherAltContact = !this.isValidContactNumber(value, this.contactInfoMotherAltCountry);
  }

  onContactInfoMotherWhatsappInput(value: string): void {
    this.contactInfoMotherWhatsapp = value;
    this.contactInfoInvalidMotherWhatsapp = !this.isValidContactNumber(value, this.contactInfoMotherWhatsappCountry);
  }

  onContactInfoGuardianAltContactInput(value: string): void {
    this.contactInfoGuardianAltContact = value;
    this.contactInfoInvalidGuardianAltContact = !this.isValidContactNumber(value, this.contactInfoGuardianAltCountry);
  }

  onContactInfoGuardianWhatsappInput(value: string): void {
    this.contactInfoGuardianWhatsapp = value;
    this.contactInfoInvalidGuardianWhatsapp = !this.isValidContactNumber(value, this.contactInfoGuardianWhatsappCountry);
  }

  onContactInfoFatherCountryChange(country: Country): void {
    this.contactInfoFatherCountry = country;
    this.contactInfoFatherCountryCode = this.toDialCodeString(country);
    this.onContactInfoFatherContactInput(this.contactInfoFatherContact);
  }

  onContactInfoMotherCountryChange(country: Country): void {
    this.contactInfoMotherCountry = country;
    this.contactInfoMotherCountryCode = this.toDialCodeString(country);
    this.onContactInfoMotherContactInput(this.contactInfoMotherContact);
  }

  onContactInfoGuardianCountryChange(country: Country): void {
    this.contactInfoGuardianCountry = country;
    this.contactInfoGuardianCountryCode = this.toDialCodeString(country);
    this.onContactInfoGuardianContactInput(this.contactInfoGuardianContact);
  }

  onContactInfoFatherAltCountryChange(country: Country): void {
    this.contactInfoFatherAltCountry = country;
    this.contactInfoFatherAltCountryCode = this.toDialCodeString(country);
    this.onContactInfoFatherAltContactInput(this.contactInfoFatherAltContact);
  }

  onContactInfoFatherWhatsappCountryChange(country: Country): void {
    this.contactInfoFatherWhatsappCountry = country;
    this.contactInfoFatherWhatsappCountryCode = this.toDialCodeString(country);
    this.onContactInfoFatherWhatsappInput(this.contactInfoFatherWhatsapp);
  }

  onContactInfoMotherAltCountryChange(country: Country): void {
    this.contactInfoMotherAltCountry = country;
    this.contactInfoMotherAltCountryCode = this.toDialCodeString(country);
    this.onContactInfoMotherAltContactInput(this.contactInfoMotherAltContact);
  }

  onContactInfoMotherWhatsappCountryChange(country: Country): void {
    this.contactInfoMotherWhatsappCountry = country;
    this.contactInfoMotherWhatsappCountryCode = this.toDialCodeString(country);
    this.onContactInfoMotherWhatsappInput(this.contactInfoMotherWhatsapp);
  }

  onContactInfoGuardianAltCountryChange(country: Country): void {
    this.contactInfoGuardianAltCountry = country;
    this.contactInfoGuardianAltCountryCode = this.toDialCodeString(country);
    this.onContactInfoGuardianAltContactInput(this.contactInfoGuardianAltContact);
  }

  onContactInfoGuardianWhatsappCountryChange(country: Country): void {
    this.contactInfoGuardianWhatsappCountry = country;
    this.contactInfoGuardianWhatsappCountryCode = this.toDialCodeString(country);
    this.onContactInfoGuardianWhatsappInput(this.contactInfoGuardianWhatsapp);
  }

  onContactInfoImageSelected(event: any, which: 'father' | 'mother' | 'guardian'): void {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    if (!file.type || !file.type.startsWith('image/')) {
      customToastr('Please select a valid image file', 'error');
      event.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = (e: any) => {
      if (which === 'father') {
        this.contactInfoFatherImagePreview = e.target.result;
        this.contactInfoFatherImageFile = file;
      } else if (which === 'mother') {
        this.contactInfoMotherImagePreview = e.target.result;
        this.contactInfoMotherImageFile = file;
      } else {
        this.contactInfoGuardianImagePreview = e.target.result;
        this.contactInfoGuardianImageFile = file;
      }
      this.contactInfoPendingImageDeletions.delete(which + 'Image');
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  }

  /* Removal is local-only until Save — the actual deletePhotoType is sent
     together with any uploads in one call to updateParentsPicture. */
  removeContactInfoImage(which: 'father' | 'mother' | 'guardian'): void {
    const photoTypeKey = which + 'Image';
    const defaultImageURL = this.contactInfoData.defaultImageURL || null;
    if (which === 'father') {
      this.contactInfoFatherImagePreview = defaultImageURL;
      this.contactInfoFatherImageFile = null;
    } else if (which === 'mother') {
      this.contactInfoMotherImagePreview = defaultImageURL;
      this.contactInfoMotherImageFile = null;
    } else {
      this.contactInfoGuardianImagePreview = defaultImageURL;
      this.contactInfoGuardianImageFile = null;
    }
    if (this.contactInfoData[photoTypeKey] && this.contactInfoData[photoTypeKey] !== defaultImageURL) {
      this.contactInfoPendingImageDeletions.add(photoTypeKey);
    }
  }

  /* Uploads/deletes whichever of father/mother/guardian pictures the user
     actually changed on this visit — untouched ones are left alone. */
  private saveContactInfoPictureIfChanged(): Promise<void> {
    if (!this.contactInfoFatherImageFile && !this.contactInfoMotherImageFile && !this.contactInfoGuardianImageFile && this.contactInfoPendingImageDeletions.size === 0) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      this.studentContactInfoService.updateParentsPicture(
        this.studentContactInfoStudentId,
        {
          fatherFile: this.contactInfoFatherImageFile,
          motherFile: this.contactInfoMotherImageFile,
          guardianFile: this.contactInfoGuardianImageFile,
        },
        Array.from(this.contactInfoPendingImageDeletions),
        localStorage.getItem('usertoken')
      ).subscribe(
        (res: any) => {
          const body = res && res.body ? res.body : {};
          if (body.fatherImage) {
            this.contactInfoFatherImagePreview = body.fatherImage;
            this.contactInfoData.fatherImage = body.fatherImage;
          }
          if (body.motherImage) {
            this.contactInfoMotherImagePreview = body.motherImage;
            this.contactInfoData.motherImage = body.motherImage;
          }
          if (body.guardianImage) {
            this.contactInfoGuardianImagePreview = body.guardianImage;
            this.contactInfoData.guardianImage = body.guardianImage;
          }
          this.contactInfoFatherImageFile = null;
          this.contactInfoMotherImageFile = null;
          this.contactInfoGuardianImageFile = null;
          const defaultImageURL = this.contactInfoData.defaultImageURL || null;
          this.contactInfoPendingImageDeletions.forEach((photoTypeKey) => {
            this.contactInfoData[photoTypeKey] = defaultImageURL;
          });
          this.contactInfoPendingImageDeletions.clear();
          resolve();
        },
        () => {
          customToastr('Failed to update student photo, please try again', 'error');
          resolve();
        }
      );
    });
  }

  async saveContactInfo(): Promise<void> {
    this.contactInfoData.mobileNumber = this.contactInfoFatherContact;
    this.contactInfoData.countryCode = this.contactInfoFatherCountryCode;
    this.contactInfoData.motherPhone = this.contactInfoMotherContact;
    this.contactInfoData.motherCountryCode = this.contactInfoMotherCountryCode;
    this.contactInfoData.guardianPhone = this.contactInfoGuardianContact;
    this.contactInfoData.guardianCountryCode = this.contactInfoGuardianCountryCode;

    this.contactInfoData.fatherAlternateNumber = this.contactInfoFatherAltContact;
    this.contactInfoData.fatherAlternateCountryCode = this.contactInfoFatherAltCountryCode;
    this.contactInfoData.fatherWhatsappNumber = this.contactInfoFatherWhatsapp;
    this.contactInfoData.fatherWhatsappCountryCode = this.contactInfoFatherWhatsappCountryCode;

    this.contactInfoData.motherAlternateNumber = this.contactInfoMotherAltContact;
    this.contactInfoData.motherAlternateCountryCode = this.contactInfoMotherAltCountryCode;
    this.contactInfoData.motherWhatsappNumber = this.contactInfoMotherWhatsapp;
    this.contactInfoData.motherWhatsappCountryCode = this.contactInfoMotherWhatsappCountryCode;

    this.contactInfoData.guardianAlternateNumber = this.contactInfoGuardianAltContact;
    this.contactInfoData.guardianAlternateCountryCode = this.contactInfoGuardianAltCountryCode;
    this.contactInfoData.guardianWhatsappNumber = this.contactInfoGuardianWhatsapp;
    this.contactInfoData.guardianWhatsappCountryCode = this.contactInfoGuardianWhatsappCountryCode;

    if (!this.contactInfoData.parentName) {
      customToastr('Please provide a valid father name', 'error');
      return;
    }
    if (!this.contactInfoFatherContact || this.contactInfoInvalidFatherContact) {
      customToastr("Please enter a valid father's mobile number", 'error');
      return;
    }
    if (this.contactInfoMotherContact && this.contactInfoInvalidMotherContact) {
      customToastr("Please enter a valid mother's mobile number", 'error');
      return;
    }
    if (this.contactInfoGuardianContact && this.contactInfoInvalidGuardianContact) {
      customToastr("Please enter a valid guardian's mobile number", 'error');
      return;
    }
    if (this.contactInfoFatherAltContact && this.contactInfoInvalidFatherAltContact) {
      customToastr("Please enter a valid father's alternate mobile number", 'error');
      return;
    }
    if (this.contactInfoFatherWhatsapp && this.contactInfoInvalidFatherWhatsapp) {
      customToastr("Please enter a valid father's WhatsApp number", 'error');
      return;
    }
    if (this.contactInfoMotherAltContact && this.contactInfoInvalidMotherAltContact) {
      customToastr("Please enter a valid mother's alternate mobile number", 'error');
      return;
    }
    if (this.contactInfoMotherWhatsapp && this.contactInfoInvalidMotherWhatsapp) {
      customToastr("Please enter a valid mother's WhatsApp number", 'error');
      return;
    }
    if (this.contactInfoGuardianAltContact && this.contactInfoInvalidGuardianAltContact) {
      customToastr("Please enter a valid guardian's alternate mobile number", 'error');
      return;
    }
    if (this.contactInfoGuardianWhatsapp && this.contactInfoInvalidGuardianWhatsapp) {
      customToastr("Please enter a valid guardian's WhatsApp number", 'error');
      return;
    }
    if (this.contactInfoData.fatherPinCode && this.contactInfoData.fatherPinCode.length < 6) {
      customToastr('Please enter a valid father pin code, it is exactly 6 digits', 'error');
      return;
    }
    if (this.contactInfoData.motherPinCode && this.contactInfoData.motherPinCode.length < 6) {
      customToastr('Please enter a valid mother pin code, it is exactly 6 digits', 'error');
      return;
    }
    if (this.contactInfoData.guardianPinCode && this.contactInfoData.guardianPinCode.length < 6) {
      customToastr('Please enter a valid guardian pin code, it is exactly 6 digits', 'error');
      return;
    }
    if (this.contactInfoData.emailId) {
      const fatherEmailIsValid = this.formValidator.validateIsEmail(this.contactInfoData.emailId);
      if (!fatherEmailIsValid) {
        customToastr('Please enter a valid father email', 'error');
        return;
      }
    }
    if (this.contactInfoData.motherEmail) {
      const motherEmailIsValid = this.formValidator.validateIsEmail(this.contactInfoData.motherEmail);
      if (!motherEmailIsValid) {
        customToastr('Please enter a valid mother email', 'error');
        return;
      }
    }
    if (!this.contactInfoData.address) {
      customToastr('Please provide father address', 'error');
      return;
    }

    const excludedFieldsFromUpdate = ['isFatherImageAllowDelete', 'isMotherImageAllowDelete', 'isGuardianImageAllowDelete', 'defaultImageURL'];
    const finalStudent: any = {};
    for (const key in this.contactInfoData) {
      if (this.contactInfoData.hasOwnProperty(key) && !excludedFieldsFromUpdate.includes(key)) {
        finalStudent[key] = (this.formValidator.validateIsDataForNumber(this.contactInfoData[key]) && this.contactInfoData[key] !== 'null') ? this.contactInfoData[key] : null;
      }
    }

    this.contactInfoSaving = true;
    await this.saveContactInfoPictureIfChanged();
    this.studentContactInfoService.updateStudentDetails(finalStudent, localStorage.getItem('usertoken')).subscribe(
      () => {
        this.contactInfoSaving = false;
        customToastr('Contact information successfully updated', 'success');
        this.afterStudentContactInfoSaved();
      },
      () => {
        this.contactInfoSaving = false;
        customToastr('Failed to update contact information, please try again', 'error');
      }
    );
  }

  afterStudentContactInfoSaved(): void {
    this.openStudentContactInfoPopup = false;
    this.fetchStudents();
  }

  studentContactInfoPopupClose(): void {
    this.openStudentContactInfoPopup = false;
    ['gv2-contactinfo-father-phone', 'gv2-contactinfo-mother-phone', 'gv2-contactinfo-guardian-phone'].forEach((id) => {
      const input = $('#' + id);
      if (input.length && typeof input.intlTelInput === 'function') {
        input.intlTelInput('destroy');
      }
    });
  }

  openTransportOptions(row: StudentRow): void {
    this.studentTransportOptionsStudentId = row.DT_RowId;
    this.studentTransportOptionsStudentName = row.studentName;
    this.openStudentTransportOptionSettingsPopup = true;
    this.closeActionMenu();
    this.resetTransportOptionsState();
    this.getTransportOptionsTerms();
  }

  private resetTransportOptionsState(): void {
    this.transportOptionsStopList = [];
    this.transportOptionsBusList = [];
    this.transportOptionsSelectedStopId = null;
    this.transportOptionsSelectedBusId = null;
    this.transportOptionsTerms = [];
    this.transportOptionsViewTermIds = [];
    this.transportOptionsSelectAll = false;
  }

  getTransportOptionsTerms(): void {
    this.transportOptionsLoading = true;
    this.studentTransportOptionsService.getStudentTermsDetail(this.studentTransportOptionsStudentId, localStorage.getItem('usertoken'), this.currentAcademicId).subscribe(
      (data: any) => {
        this.transportOptionsLoading = false;
        if (!data.success) {
          return;
        }
        if (data.data.length > 0) {
          const busOptionExisting = data.data.filter((term: any) => !!term.fkBusStopId);
          if (busOptionExisting.length > 0) {
            this.transportOptionsSelectedStopId = busOptionExisting[0].fkBusStopId || null;
            this.transportOptionsSelectedBusId = busOptionExisting[0].fkBusId || null;
          }
        }
        const sortedTerms = [...data.data].reverse();
        sortedTerms.forEach((term: any) => {
          if (term.isSelectedTerm === undefined) {
            term.isSelectedTerm = false;
          } else if (term.isSelectedTerm) {
            this.transportOptionsViewTermIds.push(term.pkBusStopTermId);
          }
          term.isPaid = !!term.isPaid;
          term.isEditable = !term.isPaid;
        });
        this.transportOptionsTerms = sortedTerms;
        this.getTransportOptionsStops();
        this.updateTransportOptionsSelectAll();
      },
      () => {
        this.transportOptionsLoading = false;
      }
    );
  }

  getTransportOptionsStops(): void {
    this.transportOptionsLoading = true;
    this.studentTransportOptionsService.fetchBusStop(localStorage.getItem('usertoken'), this.currentAcademicId).subscribe(
      (data: any) => {
        this.transportOptionsLoading = false;
        this.transportOptionsStopList = data;
        if (this.transportOptionsSelectedStopId) {
          this.getTransportOptionsBusesForStop(this.transportOptionsSelectedStopId);
        }
      },
      () => {
        this.transportOptionsLoading = false;
      }
    );
  }

  onTransportOptionsStopChange(): void {
    this.getTransportOptionsBusesForStop(this.transportOptionsSelectedStopId);
  }

  getTransportOptionsBusesForStop(stopId: any): void {
    this.transportOptionsLoading = true;
    this.studentTransportOptionsService.getBusListForStop(stopId, localStorage.getItem('usertoken'), this.currentAcademicId).subscribe(
      (data: any) => {
        this.transportOptionsLoading = false;
        this.transportOptionsBusList = data;
        if (this.transportOptionsBusList.length === 0) {
          customToastr('There are no buses passing through the stop', 'error');
        } else {
          this.transportOptionsTerms.forEach((term) => {
            if (term.isEditable) {
              term.fkBusStopId = stopId;
            }
          });
        }
      },
      () => {
        this.transportOptionsLoading = false;
        this.transportOptionsBusList = [];
      }
    );
  }

  onTransportOptionsTermChange(term: any, checked: boolean): void {
    term.isSelectedTerm = checked;
    const termId = term.pkBusStopTermId;
    const index = this.transportOptionsViewTermIds.indexOf(termId);
    if (index !== -1 && !checked) {
      this.transportOptionsViewTermIds.splice(index, 1);
    } else if (checked) {
      this.transportOptionsViewTermIds.push(termId);
    }
    this.updateTransportOptionsSelectAll();
  }

  onTransportOptionsSelectAllChange(checked: boolean): void {
    this.transportOptionsTerms.forEach((term) => {
      if (checked) {
        term.isSelectedTerm = true;
        if (this.transportOptionsViewTermIds.indexOf(term.pkBusStopTermId) === -1) {
          this.transportOptionsViewTermIds.push(term.pkBusStopTermId);
        }
      } else if (term.isEditable) {
        term.isSelectedTerm = false;
        const index = this.transportOptionsViewTermIds.indexOf(term.pkBusStopTermId);
        if (index !== -1) {
          this.transportOptionsViewTermIds.splice(index, 1);
        }
      }
    });
    this.updateTransportOptionsSelectAll();
  }

  private updateTransportOptionsSelectAll(): void {
    this.transportOptionsSelectAll = this.transportOptionsViewTermIds.length === this.transportOptionsTerms.length;
  }

  async saveTransportOptions(): Promise<void> {
    const data: any = {
      studentId: this.studentTransportOptionsStudentId,
      academicId: this.currentAcademicId,
      busId: this.transportOptionsSelectedBusId,
      stopId: this.transportOptionsSelectedStopId,
      busTermStatuses: this.transportOptionsTerms,
    };
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        data[key] = (this.formValidator.validateIsDataForNumber(data[key]) && data[key] !== 'null') ? data[key] : null;
      }
    }

    if (!(data.studentId && data.academicId && data.busId && data.stopId && data.busTermStatuses && data.busTermStatuses.length > 0)) {
      customToastr('Please provide the necessary details', 'error');
      return;
    }

    this.transportOptionsSaving = true;
    this.studentTransportOptionsService.updateStudentDetails(data, localStorage.getItem('usertoken')).subscribe(
      (res: any) => {
        this.transportOptionsSaving = false;
        if (res.status) {
          customToastr('Student transport options are updated successfully', 'success');
          this.afterTransportOptionsSaved();
        } else {
          customToastr(res.message || 'Failed to update transport options', 'error');
        }
      },
      () => {
        this.transportOptionsSaving = false;
        customToastr('There was an error while updating student transport options, please try again', 'error');
      }
    );
  }

  afterTransportOptionsSaved(): void {
    this.openStudentTransportOptionSettingsPopup = false;
    this.fetchStudents();
  }

  transportOptionsPopupClose(): void {
    this.openStudentTransportOptionSettingsPopup = false;
  }

  initiateDeactivateStudent(row: StudentRow): void {
    this.studentDeactivatePopupStudentId = row.DT_RowId;
    this.studentDeactivatePopupStudentName = row.studentName;
    this.openStudentDeactivatePopup = true;
    this.closeActionMenu();
    this.deactivateSelectedReasonId = null;
    this.deactivateReasons = [];
    this.deactivateProcessing = false;
    this.deactivateShowConfirm = false;
    this.getDeactivateReasonList();
  }

  getDeactivateReasonList(): void {
    this.deactivateReasonsLoading = true;
    this.studentDeactivationService.getDeactivateReasonList().subscribe(
      (data: any) => {
        this.deactivateReasonsLoading = false;
        this.deactivateReasons = data;
      },
      () => {
        this.deactivateReasonsLoading = false;
      }
    );
  }

  confirmDeactivateStudent(): void {
    if (!this.deactivateSelectedReasonId || this.deactivateSelectedReasonId === 'null') {
      customToastr('Please select a reason for deactivation', 'error');
      return;
    }
    this.deactivateShowConfirm = true;
  }

  deactivateStudent(): void {
    this.deactivateProcessing = true;
    this.studentDeactivationService.deactivateStudent(this.studentDeactivatePopupStudentId, this.deactivateSelectedReasonId, this.currentAcademicId).subscribe(
      (data: any) => {
        this.deactivateProcessing = false;
        if (data.status) {
          customToastr('Student successfully deactivated', 'success');
          this.afterStudentDeactivated();
        } else {
          customToastr('Failed to deactivate student, please try again', 'error');
        }
      },
      () => {
        this.deactivateProcessing = false;
        customToastr('Failed to deactivate student, please try again', 'error');
      }
    );
  }

  afterStudentDeactivated(): void {
    this.studentDeactivatePopupClose();
    this.getGroupsClassCategories();
    this.fetchStudents();
  }

  studentDeactivatePopupClose(): void {
    this.studentDeactivatePopupStudentId = null;
    this.studentDeactivatePopupStudentName = null;
    this.openStudentDeactivatePopup = false;
    this.deactivateShowConfirm = false;
  }

  // ------------------------------------------------------------------
  // Document hub - native groups-v2 modal (list + search + upload + delete
  // + preview), reusing the same GroupService methods the legacy
  // documentHub() flow in groups.component.ts calls.
  // ------------------------------------------------------------------
  documentHub(row: StudentRow): void {
    this.documentHubStudentId = row.DT_RowId;
    this.documentHubStudentName = row.studentName;
    this.documentHubRegistrationId = row.registrationId;
    this.documentHubSearchQuery = '';
    this.documentHubShowUpload = false;
    this.documentHubOpenActionIndex = '';
    this.openDocumentHubPopup = true;
    this.closeActionMenu();
    this.getUploadedDocumentOnHub();
    // Fetched up front (not just when opening the upload form) so the list
    // view's medical-documents table can show that type's own
    // labelOfTitle/labelOfDescription as column headers instead of generic
    // "Title"/"Description".
    this.getDocumentHubTypes();
  }

  closeDocumentHubPopup(): void {
    this.openDocumentHubPopup = false;
    this.documentHubUploadedList = [];
    this.documentHubData = [];
    this.documentHubShowUpload = false;
    this.documentHubOpenActionIndex = '';
  }

  async getUploadedDocumentOnHub(): Promise<void> {
    try {
      this.fetchingDocumentHub = true;
      const response: any = await firstValueFrom(
        this.groupservice.getUploadedDocumentInfo(this.documentHubStudentId, this.currentAcademicId)
      );
      const list = response?.status && response?.body?.uploadedDocumentsList
        ? response.body.uploadedDocumentsList
        : [];
      this.documentHubUploadedList = list;
      this.documentHubData = list;
      this.fetchingDocumentHub = false;
    } catch (error) {
      this.documentHubUploadedList = [];
      this.documentHubData = [];
      this.fetchingDocumentHub = false;
    }
  }

  searchDocumentHubByTitle(searchQuery: string): void {
    this.documentHubSearchQuery = searchQuery;
    const query = (searchQuery || '').toLowerCase();
    this.documentHubData = this.documentHubUploadedList.filter((doc) =>
      (doc.documentTypeName || '').toLowerCase().includes(query)
    );
  }

  // Medical documents (documentCategorySlug === 'medical_documents', e.g. the
  // "Medical Document" type) get their own table in the same Document Hub
  // modal, separate from every other ("normal") document type — per-category
  // split driven by the API's category slug rather than a hardcoded type-id
  // check, so any future type tagged with this slug is handled the same way.
  private static readonly MEDICAL_DOCUMENT_CATEGORY_SLUG = 'medical_documents';

  get documentHubNormalData(): any[] {
    return this.documentHubData.filter((doc) => doc.documentCategorySlug !== GroupsV2Component.MEDICAL_DOCUMENT_CATEGORY_SLUG);
  }

  get documentHubMedicalData(): any[] {
    return this.documentHubData.filter((doc) => doc.documentCategorySlug === GroupsV2Component.MEDICAL_DOCUMENT_CATEGORY_SLUG);
  }

  // Column headers for the medical table use that document type's own
  // labelOfTitle/labelOfDescription (e.g. "Disease Name"/"Disease
  // Description") when available, falling back to the generic Title/
  // Description labels if the type list hasn't loaded yet or doesn't define
  // them. documentTypeList (unlike the uploaded-documents list) has no
  // documentCategorySlug field, so the medical type is identified by being
  // the one with label overrides configured — in practice "Medical Document" —
  // rather than hardcoding that name.
  get medicalDocumentType(): any {
    return this.documentHubTypesInfo.find((t) => t.labelOfTitle || t.labelOfDescription);
  }

  // index is a plain number for the normal-documents table and an 'm'-prefixed
  // string (e.g. 'm0') for the medical-documents table, keeping their row
  // states from colliding when both tables render side by side.
  openDocumentHubActions(index: number | string): void {
    this.documentHubOpenActionIndex = this.documentHubOpenActionIndex === index ? '' : index;
  }

  closeDocumentHubActions(): void {
    this.documentHubOpenActionIndex = '';
  }

  previewDocumentHubDoc(doc: any, index: number | string): void {
    if (this.documentHubDeleting || this.documentHubPreviewingIndex !== '') {
      return;
    }
    this.documentHubPreviewingIndex = index;
    this.documentHubOpenActionIndex = '';
    fetch(AppSettings.API_ENDPOINT_V3 + apiConstants.previewDoc, {
      method: 'GET',
      headers: {
        authorization: this.getAuthToken,
        documentid: doc.pkStudentDocumentsId,
        lang: 'en',
        schoolid: this.getSchoolId,
        academicId: this.currentAcademicId,
        studentId: this.documentHubStudentId,
      } as any,
    })
      .then((response) => (response.ok ? response.blob() : undefined))
      .then((blob) => {
        this.documentHubPreviewingIndex = '';
        if (!blob) {
          customToastr('Failed to preview document, please try again', 'error');
          return;
        }
        const objectUrl = window.URL.createObjectURL(blob);
        this.docPreviewObjectUrl = objectUrl;
        const isPdf = blob.type === 'application/pdf';
        this.openDocPreview(objectUrl, isPdf, doc.documentTitle || doc.documentTypeName || '');
      })
      .catch(() => {
        this.documentHubPreviewingIndex = '';
        customToastr('Failed to preview document, please try again', 'error');
      });
  }

  async deleteDocumentHubDoc(doc: any): Promise<void> {
    try {
      this.documentHubDeleting = true;
      const body = {
        academicId: this.currentAcademicId,
        documentId: doc.pkStudentDocumentsId,
        lang: 'en',
        studentId: this.documentHubStudentId,
      };
      const response: any = await firstValueFrom(this.groupservice.deleteUploadedDoc(body));
      if (response?.status && response?.body) {
        customToastr(response.body.message || 'Document successfully deleted', 'success');
        await this.getUploadedDocumentOnHub();
        if (this.documentHubSearchQuery) {
          this.searchDocumentHubByTitle(this.documentHubSearchQuery);
        }
      }
      this.documentHubDeleting = false;
      this.closeDocumentHubActions();
    } catch (error) {
      customToastr('Failed to delete document, please try again', 'error');
      this.documentHubDeleting = false;
      this.closeDocumentHubActions();
    }
  }

  toggleDocumentHubUpload(): void {
    this.documentHubOpenActionIndex = '';
    this.documentHubShowUpload = !this.documentHubShowUpload;
    if (this.documentHubShowUpload) {
      this.documentHubTypesInfo = [];
      this.documentHubSelectedType = null;
      this.documentHubSelectedFile = null;
      this.getDocumentHubTypes();
    } else {
      this.getUploadedDocumentOnHub();
    }
  }

  // 'Other' used to be the only document type with a mandatory title (hardcoded
  // by name). The API now drives this generically per type via isLabelRequired/
  // isDescriptionRequired (e.g. "Medical Document" requires both, with its own
  // labelOfTitle/labelOfDescription text) — '1' means required, matching the
  // string-flag convention used elsewhere in this app's feature/permission data.
  get isDocumentHubTitleRequired(): boolean {
    return this.documentHubSelectedType?.isLabelRequired === '1'
      || this.documentHubSelectedType?.documentTypeName === 'Other';
  }

  get isDocumentHubDescriptionRequired(): boolean {
    return this.documentHubSelectedType?.isDescriptionRequired === '1';
  }

  async getDocumentHubTypes(): Promise<void> {
    try {
      this.documentHubFetchingTypes = true;
      const response: any = await firstValueFrom(this.groupservice.getDocumentType());
      this.documentHubTypesInfo = response?.status && response?.body?.documentTypeList
        ? response.body.documentTypeList
        : [];
      this.documentHubFetchingTypes = false;
    } catch (error) {
      this.documentHubTypesInfo = [];
      this.documentHubFetchingTypes = false;
      customToastr('Failed to fetch document types, please try again', 'error');
    }
  }

  onDocumentHubFileSelected(event: any): void {
    if (this.documentHubSelectedType == null) {
      event.target.value = '';
      customToastr('Please select the document type first', 'error');
      return;
    }
    this.documentHubSelectedFile = event.target.files[0];
  }

  async uploadDocumentHubFile(title: string, description: string): Promise<void> {
    try {
      if (this.documentHubSelectedType == null) {
        customToastr('Please select the document type first', 'error');
        return;
      }
      if (this.isDocumentHubTitleRequired && (title || '').trim() === '') {
        customToastr(`Please provide the ${this.documentHubSelectedType.labelOfTitle || 'title'} of the document`, 'error');
        return;
      }
      if (this.isDocumentHubDescriptionRequired && (description || '').trim() === '') {
        customToastr(`Please provide the ${this.documentHubSelectedType.labelOfDescription || 'description'} of the document`, 'error');
        return;
      }
      if (this.documentHubSelectedFile == null) {
        customToastr('Please upload the document first', 'error');
        return;
      }
      const mediaSizeInMb = this.documentHubSelectedFile.size / (1024 * 1024);
      if (mediaSizeInMb > this.documentHubSelectedType.mediaSize) {
        customToastr(`Please upload the document less than ${this.documentHubSelectedType.mediaSize} MB.`, 'error');
        return;
      }
      this.documentHubUploading = true;
      const body = {
        lang: 'en',
        academicId: this.currentAcademicId,
        documentTitle: title,
        documentDescription: description,
        documentTypeId: this.documentHubSelectedType.pkDocumentTypeId,
        studentId: this.documentHubStudentId,
      };
      const response: any = await firstValueFrom(
        this.groupservice.uploadDocumentForDocumentHub(body, this.documentHubSelectedFile)
      );
      if (response?.status && response?.body) {
        customToastr(response.body.message || 'Document successfully uploaded', 'success');
      }
      this.documentHubUploading = false;
      this.toggleDocumentHubUpload();
    } catch (error) {
      this.documentHubUploading = false;
      customToastr('Failed to upload document, please try again', 'error');
    }
  }

  // ------------------------------------------------------------------
  // Student update history - minimal native modal, reuses
  // groupservice.fetchStudentUpdateHistory({ academicId, studentId }).
  // ------------------------------------------------------------------
  getStudentUpdateHistory(row: StudentRow): void {
    this.studentUpdateHistoryList = [];
    this.studentUpdateHistoryStudentName = '';
    this.studentUpdateHistoryRegistrationId = '';
    this.historySearchTerm = '';
    this.historyPageIndex = 0;
    this.historyPageSize = 10;
    this.openStudentUpdateHistoryPopup = true;
    this.fetchingStudentUpdateHistory = true;

    const params = {
      academicId: this.currentAcademicId,
      studentId: row.DT_RowId,
    };
    this.groupservice.fetchStudentUpdateHistory(params).subscribe(
      (data: any) => {
        if (!data.errorcode) {
          this.studentUpdateHistoryStudentName = data.studentInfo?.studentName || '';
          this.studentUpdateHistoryRegistrationId = data.studentInfo?.registrationId || '';
          this.studentUpdateHistoryList = data.studentUpdateHistory || [];
        }
        this.fetchingStudentUpdateHistory = false;
      },
      () => {
        this.fetchingStudentUpdateHistory = false;
      }
    );
  }

  closeStudentUpdateHistoryPopup(): void {
    this.openStudentUpdateHistoryPopup = false;
  }

  formatHistoryColumnName(columnName: string): string {
    const result = columnName.replace(/([A-Z])/g, ' $1');
    return result.charAt(0).toUpperCase() + result.slice(1);
  }

  // ------------------------------------------------------------------
  // Print / profile view popup (app-student-profile-view)
  // ------------------------------------------------------------------
  openStudentProfileView(row: StudentRow): void {
    this.studentProfileViewStudentId = row.DT_RowId;
    this.studentProfileViewStudentName = row.studentName;
    this.studentProfile = {};
    this.profileAcademicInfo = {};
    this.openStudentProfileViewPopup = true;
    this.fetchingStudentProfile = true;

    this.studentProfileViewService.getStudentProfilePrintDetails(
      this.studentProfileViewStudentId,
      localStorage.getItem('usertoken')
    ).subscribe(
      (data: any) => {
        this.studentProfile = data.studentInfo || {};
        this.profileAcademicInfo = data.academicInfo || {};
        this.fetchingStudentProfile = false;
      },
      () => {
        this.fetchingStudentProfile = false;
      }
    );
  }

  studentProfileViewClose(): void {
    this.openStudentProfileViewPopup = false;
  }

  // ------------------------------------------------------------------
  // Add Student - opens the same shared New Admission popup (<app-admission-v2>,
  // Bootstrap modal id #newadmission-v2) used app-wide, mounted once via
  // header-v2.component.html -> layout.component.html so it's already present
  // on every page including this one. Same trigger mechanism as the "New
  // Admission" buttons in header-v2 and dashboard-v2 (data-toggle="modal"
  // data-target="#newadmission-v2"); shown() here as well so it also works
  // if the data-attribute click delegation doesn't fire (e.g. this <li> sits
  // inside an *ngIf-toggled dropdown).
  //
  // Import Student - the OLD groups.component's import flow was a large
  // inline jQuery offcanvas form (500+ lines) tightly coupled to that
  // component's own widgets, with no reusable shared component to open
  // instead. Rebuilding it natively is out of scope for this first pass
  // since the task explicitly prioritizes the list/filter/search/sort/
  // pagination/row-action experience, so it stays a "coming soon" stub.
  // ------------------------------------------------------------------
  addNewStudentButton(): void {
    this.closeActionMenu();
    $('#newadmission-v2').modal('show');
  }

  // ------------------------------------------------------------------
  // Import Student - native groups-v2 popup that replaces the legacy inline
  // jQuery flow (#bulkUploadSTudent modal + #import-student-canvas offcanvas
  // in groups.component.html). Same logic/validation/APIs as the original
  // (groupsAvailableClass/groupsAvailableClassSection for the cascading
  // pickers, raw POST to /uploadxlsjson for the bulk upload, and
  // GroupService.validateJsonDataOnProceed for the final duplicate-check/
  // commit step) - just reimplemented with Angular data binding instead of
  // the original's manual jQuery DOM rewriting (turning <td> cells into
  // raw HTML inputs and serializing the table back to JSON via DOM
  // traversal), since that approach doesn't translate to this component's
  // architecture. The resulting request payload shape is unchanged.
  // ------------------------------------------------------------------
  openImportStudentPopup = false;
  importStudentStep: 'upload' | 'results' = 'upload';
  importStudentCategoryList: any[] = [];
  importStudentClassList: any[] = [];
  importStudentSectionList: any[] = [];
  importStudentSelectedCategory: any = null;
  importStudentSelectedClass: any = null;
  importStudentSelectedSection: any = null;
  importStudentSelectedFile: File | null = null;
  importStudentUploading = false;
  importStudentCommitting = false;
  importStudentRows: any[] = [];
  private static readonly IMPORT_STUDENT_COLUMNS = [
    { key: 'studentClassId', statusKey: 'studentClassIdStatus', label: 'Class Id', labelKey: 'groupsV2.colClassId', type: 'text' },
    { key: 'studentName', statusKey: 'studentNameStatus', label: 'Student Name', labelKey: 'groupsV2.colStudentName', type: 'text' },
    { key: 'dateOfBirth', statusKey: 'dateOfBirthStatus', label: 'Date Of Birth', labelKey: 'groupsV2.colDateOfBirthImport', type: 'date' },
    { key: 'studentGender', statusKey: 'studentGenderStatus', label: 'Gender', labelKey: 'groupsV2.colGender', type: 'gender' },
    { key: 'admissionId', statusKey: 'admissionIdStatus', label: 'Admission Id', labelKey: 'groupsV2.colAdmissionId', type: 'text' },
    { key: 'dateOfAdmission', statusKey: 'dateOfAdmissionStatus', label: 'Date Of Admission', labelKey: 'groupsV2.colDateOfAdmission', type: 'date' },
    { key: 'parentName', statusKey: 'parentNameStatus', label: 'Parent Name', labelKey: 'groupsV2.colParentName', type: 'text' },
    { key: 'parentContactNumber', statusKey: 'parentContactNumberStatus', label: 'Parent Contact', labelKey: 'groupsV2.colParentContact', type: 'numeric' },
    { key: 'parentEmail', statusKey: 'parentEmailStatus', label: 'Parent Email', labelKey: 'groupsV2.colParentEmail', type: 'text' },
    { key: 'countryCode', statusKey: 'countryCodeStatus', label: 'Country Code', labelKey: 'groupsV2.colCountryCode', type: 'numeric' },
  ];

  get importStudentColumns() {
    return GroupsV2Component.IMPORT_STUDENT_COLUMNS;
  }

  openStudentImportForm(): void {
    this.closeActionMenu();
    this.importStudentStep = 'upload';
    this.importStudentCategoryList = [];
    this.importStudentClassList = [];
    this.importStudentSectionList = [];
    this.importStudentSelectedCategory = null;
    this.importStudentSelectedClass = null;
    this.importStudentSelectedSection = null;
    this.importStudentSelectedFile = null;
    this.importStudentRows = [];
    this.openImportStudentPopup = true;
    this.groupservice.groupsClassCategories(this.getSchoolId, this.getAuthToken).subscribe(
      (data: any) => {
        this.importStudentCategoryList = data.schoolCategory || [];
      },
      () => {}
    );
  }

  closeImportStudentPopup(): void {
    this.openImportStudentPopup = false;
  }

  onImportStudentCategoryChange(): void {
    this.importStudentClassList = [];
    this.importStudentSectionList = [];
    this.importStudentSelectedClass = null;
    this.importStudentSelectedSection = null;
    if (!this.importStudentSelectedCategory) {
      return;
    }
    this.groupservice.groupsAvailableClass(this.getSchoolId, this.importStudentSelectedCategory, this.getAuthToken).subscribe(
      (data: any) => {
        this.importStudentClassList = data.className || [];
      },
      () => {}
    );
  }

  onImportStudentClassChange(): void {
    this.importStudentSectionList = [];
    this.importStudentSelectedSection = null;
    if (!this.importStudentSelectedClass) {
      return;
    }
    this.groupservice.groupsAvailableClassSection(this.importStudentSelectedClass, this.getAuthToken).subscribe(
      (data: any) => {
        this.importStudentSectionList = data || [];
      },
      () => {}
    );
  }

  onImportStudentFileSelected(event: any): void {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    if (!this.setImportStudentFile(file)) {
      event.target.value = '';
    }
  }

  importStudentDragOver = false;

  onImportStudentDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.importStudentDragOver = true;
  }

  onImportStudentDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.importStudentDragOver = false;
  }

  onImportStudentDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.importStudentDragOver = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.setImportStudentFile(file);
    }
  }

  private setImportStudentFile(file: File): boolean {
    const extension = (file.name.split('.').pop() || '').toLowerCase();
    if (extension !== 'xls' && extension !== 'xlsx') {
      customToastr('File format should be in xls or xlsx format, download the template file and use it', 'error');
      return false;
    }
    this.importStudentSelectedFile = file;
    return true;
  }

  importStudentProceed(): void {
    if (!this.importStudentSelectedSection) {
      customToastr('Please select the class', 'error');
      return;
    }
    if (!this.importStudentSelectedFile) {
      customToastr('Please select a file to upload', 'error');
      return;
    }

    this.importStudentUploading = true;
    const formData: FormData = new FormData();
    formData.append('uploadfile', this.importStudentSelectedFile, this.importStudentSelectedFile.name);
    const headers = new HttpHeaders().append('authToken', this.getAuthToken);

    this.http.post(
      AppSettings.API_ENDPOINT + '/uploadxlsjson?schoolId=' + this.getSchoolId + '&classId=' + this.importStudentSelectedSection,
      formData,
      { headers }
    ).subscribe(
      (data: any) => {
        this.importStudentUploading = false;
        if (data.errorcode === 10037) {
          customToastr('Only 100 students are allowed to add in trial subscription', 'error');
          return;
        }
        if (data.sqlMessage) {
          customToastr(data.sqlMessage + ' Please check the excel sheet', 'error');
          return;
        }
        this.importStudentRows = (data || []).map((row: any) => ({ ...row, editing: false }));
        this.importStudentStep = 'results';
      },
      () => {
        this.importStudentUploading = false;
        customToastr('Something went wrong, please try again', 'error');
      }
    );
  }

  importStudentRowHasError(row: any): boolean {
    return GroupsV2Component.IMPORT_STUDENT_COLUMNS.some((col) => row[col.statusKey] == '1');
  }

  get importStudentHasErrors(): boolean {
    return this.importStudentRows.some((row) => this.importStudentRowHasError(row));
  }

  get importStudentIsEditing(): boolean {
    return this.importStudentRows.some((row) => row.editing);
  }

  resolveImportStudentErrors(): void {
    this.importStudentRows.forEach((row) => {
      row.editing = this.importStudentRowHasError(row);
    });
  }

  deleteImportStudentRow(index: number): void {
    this.importStudentRows.splice(index, 1);
  }

  private isValidImportEmail(email: string): boolean {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email);
  }

  private revalidateImportStudentRow(row: any, classIdCounts: { [key: string]: number }, admissionIdCounts: { [key: string]: number }): void {
    GroupsV2Component.IMPORT_STUDENT_COLUMNS.forEach((col) => {
      const value = row[col.key];
      const isEmpty = value === null || value === undefined || `${value}`.replace(/\s/g, '').length === 0;
      let hasError = isEmpty;
      if (!isEmpty) {
        if (col.key === 'studentClassId' && classIdCounts[`${value}`.trim()] > 1) {
          hasError = true;
        }
        if (col.key === 'admissionId' && admissionIdCounts[`${value}`.trim()] > 1) {
          hasError = true;
        }
        if (col.key === 'parentEmail' && !this.isValidImportEmail(value)) {
          hasError = true;
        }
        if (col.key === 'parentContactNumber' && !/^[0-9]+$/.test(`${value}`.trim())) {
          hasError = true;
        }
      }
      row[col.statusKey] = hasError ? '1' : '0';
    });
  }

  private buildImportStudentDuplicateCounts(): { classIdCounts: { [key: string]: number }; admissionIdCounts: { [key: string]: number } } {
    const classIdCounts: { [key: string]: number } = {};
    const admissionIdCounts: { [key: string]: number } = {};
    this.importStudentRows.forEach((row) => {
      const classId = (row.studentClassId ?? '').toString().trim();
      const admissionId = (row.admissionId ?? '').toString().trim();
      if (classId) {
        classIdCounts[classId] = (classIdCounts[classId] || 0) + 1;
      }
      if (admissionId) {
        admissionIdCounts[admissionId] = (admissionIdCounts[admissionId] || 0) + 1;
      }
    });
    return { classIdCounts, admissionIdCounts };
  }

  finishEditingImportStudentRow(row: any): void {
    const { classIdCounts, admissionIdCounts } = this.buildImportStudentDuplicateCounts();
    this.revalidateImportStudentRow(row, classIdCounts, admissionIdCounts);
    row.editing = false;
  }

  resolveImportStudentProceedData(): void {
    const { classIdCounts, admissionIdCounts } = this.buildImportStudentDuplicateCounts();
    this.importStudentRows.forEach((row) => {
      this.revalidateImportStudentRow(row, classIdCounts, admissionIdCounts);
      row.editing = false;
    });

    if (this.importStudentHasErrors) {
      customToastr('Please resolve the errors', 'error');
    } else {
      customToastr('All are validated, please proceed to import', 'success');
    }
  }

  validateImportStudentByDuplicate(): void {
    this.importStudentCommitting = true;
    const payload = this.importStudentRows.map((row) => {
      const { editing, ...rest } = row;
      return rest;
    });
    this.groupservice.validateJsonDataOnProceed(
      JSON.stringify(payload),
      this.getSchoolId,
      this.importStudentSelectedSection,
      this.currentAcademicId,
      this.getAuthToken
    ).subscribe(
      (data: any) => {
        this.importStudentCommitting = false;
        if (data && data.sqlMessage) {
          customToastr(data.sqlMessage + ' Please provide valid details', 'error');
          this.importStudentRows.forEach((row) => (row.editing = true));
        } else if (data && data[0] && data[0].status === false) {
          customToastr(data[0].message, 'error');
          this.importStudentRows.forEach((row) => (row.editing = true));
        } else {
          customToastr('Student imported successfully', 'success');
          this.getGroupsClassCategories();
          this.closeImportStudentPopup();
          this.fetchStudents();
        }
      },
      () => {
        this.importStudentCommitting = false;
        customToastr('Something went wrong, please try again', 'error');
        this.importStudentRows.forEach((row) => (row.editing = true));
      }
    );
  }

  // ------------------------------------------------------------------
  // Print-ID checkbox selection + self-contained bulk ID card feature.
  // Does NOT depend on header-v2's floating buttons or shared DOM element -
  // reuses the same HeaderService methods header-v2.component.ts's
  // openIdCard()/getTemplateIdCard()/generateIdCard() call directly.
  // ------------------------------------------------------------------
  isStudentSelected(row: StudentRow): boolean {
    const id = parseInt(row.DT_RowId as any, 10);
    return this.selectedStudentIds.indexOf(id) !== -1;
  }

  toggleStudentSelection(row: StudentRow, checked: boolean): void {
    const id = parseInt(row.DT_RowId as any, 10);
    if (isNaN(id)) {
      return;
    }
    if (checked) {
      if (this.selectedStudentIds.indexOf(id) === -1) {
        this.selectedStudentIds.push(id);
      }
    } else {
      const idx = this.selectedStudentIds.indexOf(id);
      if (idx !== -1) {
        this.selectedStudentIds.splice(idx, 1);
      }
    }
  }

  get allVisibleSelected(): boolean {
    return this.students.length > 0 && this.students.every((row) => this.isStudentSelected(row));
  }

  toggleSelectAllVisible(checked: boolean): void {
    for (const row of this.students) {
      this.toggleStudentSelection(row, checked);
    }
  }

  clearSelection(): void {
    this.selectedStudentIds = [];
  }

  async openIdCardBuilder(): Promise<void> {
    if (this.selectedStudentIds.length === 0) {
      return;
    }
    this.openIdCardPopup = true;
    this.isFetchingIdCard = true;
    try {
      const response: any = await firstValueFrom(this.headerservice.getIdCardTemplate());
      this.idCardTemplates = response?.status ? (response.body?.idCardTemplateList || []) : [];
    } catch (error) {
      this.idCardTemplates = [];
    }
    this.isFetchingIdCard = false;
  }

  closeIdCardBuilder(): void {
    this.openIdCardPopup = false;
    this.isIdCardGenerated = false;
    this.idCardTemplates = [];
  }

  selectIdCardTemplate(templateId: any): void {
    this.idCardTemplates = this.idCardTemplates.map((tpl) => ({
      ...tpl,
      isSelected: tpl.templateId === templateId,
    }));
  }

  async generateIdCardForSelection(): Promise<void> {
    const selectedTemplate = this.idCardTemplates.find((tpl) => tpl.isSelected);
    if (!selectedTemplate) {
      if (typeof customToastr === 'function') {
        customToastr('Please select a template first', 'error');
      }
      return;
    }
    this.isGeneratingId = true;
    try {
      const body = {
        lang: 'en',
        studentIdList: this.selectedStudentIds.map((id) => String(id)),
        academicId: this.currentAcademicId,
        templateId: selectedTemplate.templateId,
      };
      const response: any = await firstValueFrom(this.headerservice.generateIdCard(body));
      if (response?.status) {
        this.isIdCardGenerated = true;
      }
    } catch (error) {
      this.isIdCardGenerated = false;
      if (typeof customToastr === 'function') {
        customToastr(error?.error?.message || 'Something went wrong', 'error');
      }
    }
    this.isGeneratingId = false;
  }

  // Matches header.component.ts's navigateToIdCardPage() - the ID card
  // "preview" is the same shared jobs-list page (/id-card/student) that
  // lists every previously-generated batch, not an inline preview.
  viewGeneratedIdCards(): void {
    this.closeIdCardBuilder();
    this.clearSelection();
    this.router.navigate(['/id-card', 'student']);
  }

  // ------------------------------------------------------------------
  // TC (Transfer Certificate) generation - same HeaderService endpoint and
  // post-generate navigation (/tc) as header.component.ts's
  // generateTcSelectedStudent()/openTC(), triggered from groups-v2's own
  // selection toolbar instead of header-v2's floating button.
  // ------------------------------------------------------------------
  tcJobId: any = '';

  openTcBuilder(): void {
    if (this.selectedStudentIds.length === 0) {
      return;
    }
    this.isTcGenerated = false;
    this.tcJobId = '';
    this.openTcPopup = true;
  }

  closeTcBuilder(): void {
    this.openTcPopup = false;
    this.isTcGenerated = false;
    this.tcJobId = '';
  }

  async generateTcForSelection(): Promise<void> {
    this.isGeneratingTc = true;
    try {
      const response: any = await firstValueFrom(
        this.headerservice.generateTcForStudent(this.selectedStudentIds.map((id) => Number(id)), this.currentAcademicId)
      );
      if (response?.status && response?.body) {
        this.tcJobId = response.body.jobDetails ? (response.body.jobDetails.jobId || '') : '';
        this.isTcGenerated = true;
      } else {
        customToastr('Something went wrong while generating the transfer certificate', 'error');
      }
    } catch (error: any) {
      customToastr(error?.error?.message || 'Something went wrong while generating the transfer certificate', 'error');
    }
    this.isGeneratingTc = false;
  }

  // Matches header.component.ts's openTC() - navigates to the same shared
  // TC jobs-list/preview page.
  viewGeneratedTc(): void {
    this.closeTcBuilder();
    this.clearSelection();
    this.router.navigate(['/tc']);
  }

  // ------------------------------------------------------------------
  // Staffs tab
  // ------------------------------------------------------------------
  switchTab(tab: 'student' | 'staff'): void {
    this.activeTab = tab;
    if (tab === 'staff' && !this.staffLoadedOnce) {
      this.staffLoadedOnce = true;
      this.fetchStaff();
    }
  }

  trackByRowId(_index: number, row: StudentRow): any {
    return row.DT_RowId;
  }

  // Table state
  staff: StaffRow[] = [];
  staffTotalRecords = 0;
  loadingStaff = true;
  staffLoadedOnce = false;

  staffPageIndex = 0;
  staffPageSize = 25;

  staffSearchTerm = '';
  private staffSearchInput$ = new Subject<string>();

  staffSortColumn: StaffSortColumnKey | null = null;
  staffSortDirection: 'asc' | 'desc' = 'asc';

  // Filters
  schoolDesignationList: DesignationItem[] = [];
  staffFilterDesignation: any = 'all';
  staffFilterAppStatus: any = 'all';

  onStaffSearchInput(value: string): void {
    this.staffSearchInput$.next(value);
  }

  clearStaffSearch(): void {
    this.staffSearchTerm = '';
    this.staffSearchInput$.next('');
  }

  onStaffSortClick(column: StaffSortColumnKey): void {
    if (this.staffSortColumn === column) {
      this.staffSortDirection = this.staffSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.staffSortColumn = column;
      this.staffSortDirection = 'asc';
    }
    this.fetchStaff();
  }

  onStaffPaginatorChange(event: { first?: number; rows?: number; page?: number }): void {
    this.staffPageSize = event.rows ?? this.staffPageSize;
    this.staffPageIndex = event.page ?? 0;
    this.fetchStaff();
  }

  onStaffDesignationFilterChange(designationId: any): void {
    this.staffFilterDesignation = designationId;
    this.staffPageIndex = 0;
    this.fetchStaff();
  }

  onStaffStatusFilterChange(appStatus: any): void {
    this.staffFilterAppStatus = appStatus;
    this.staffPageIndex = 0;
    this.fetchStaff();
  }

  get hasActiveStaffFilters(): boolean {
    return this.staffFilterDesignation !== 'all' || this.staffFilterAppStatus !== 'all';
  }

  clearStaffFilters(): void {
    this.staffFilterDesignation = 'all';
    this.staffFilterAppStatus = 'all';
    this.staffPageIndex = 0;
    this.fetchStaff();
  }

  // Export Staff to Excel - same server-side, full-filtered-dataset export
  // as groups.component.ts's exportStaffToExcel() (POST .../stafflist/export,
  // blob xlsx response), just re-skinned. Unlike the student tab's client-
  // side CSV/TSV export (current page only), this already existed and does
  // more, so it's kept as-is rather than switched to the client-side trick.
  exportingStaff = false;

  exportStaffToExcel(): void {
    if (this.exportingStaff || this.staffTotalRecords === 0) {
      return;
    }
    let designationId: number[] | null = null;
    if (this.staffFilterDesignation !== 'all') {
      const id = parseInt(this.staffFilterDesignation, 10);
      if (!isNaN(id)) {
        designationId = [id];
      }
    }
    let appStatus: number | null = null;
    if (this.staffFilterAppStatus !== 'all') {
      const status = parseInt(this.staffFilterAppStatus, 10);
      if (!isNaN(status)) {
        appStatus = status;
      }
    }
    this.exportingStaff = true;
    this.groupservice.exportStaffList(designationId, appStatus).subscribe({
      next: (blob: any) => {
        this.exportingStaff = false;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'staff-list.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
        customToastr('File downloaded successfully', 'success');
      },
      error: (err: any) => {
        this.exportingStaff = false;
        customToastr(err?.error?.message || err?.message || 'Export failed.', 'error');
      }
    });
  }

  getSchoolDesignationList(): void {
    this.groupservice.groupsSchoolDesignationList(this.getSchoolId, this.getAuthToken).subscribe(
      (data: any) => {
        this.schoolDesignationList = data.designationList || [];
      },
      () => {}
    );
  }

  formatStaffColumnValue(field: string, row: StaffRow): string {
    const value = row[field];
    if (field === 'gender') {
      return value === 0 ? this.translate.instant('groupsV2.female') : value === 1 ? this.translate.instant('groupsV2.male') : '—';
    }
    if (field === 'isAppUser') {
      return value === 0 ? this.translate.instant('groupsV2.inactive') : value === 1 ? this.translate.instant('groupsV2.active') : '—';
    }
    return value ?? '—';
  }

  // ------------------------------------------------------------------
  // Core fetch - POSTs to the exact same /stafflist/searchStaff endpoint
  // with the exact same body shape as groups.component.ts's
  // initDataTableServerProcessStaffs ajax.data() function:
  //   { schoolId, designationId, appStatus, countFrom, lengthOf, search, sort }
  // and the same headers (Content-Type: application/json, authToken).
  // ------------------------------------------------------------------
  fetchStaff(): void {
    this.loadingStaff = true;

    const order = this.staffSortColumn
      ? [{ column: STAFF_SORT_COLUMN_INDEX[this.staffSortColumn], dir: this.staffSortDirection }]
      : [];

    const designationId = this.staffFilterDesignation !== 'all' ? parseInt(this.staffFilterDesignation, 10) : null;
    const appStatus = this.staffFilterAppStatus !== 'all' ? parseInt(this.staffFilterAppStatus, 10) : null;

    const body = {
      schoolId: localStorage.getItem('schoolinfo'),
      designationId,
      appStatus,
      countFrom: this.staffPageIndex * this.staffPageSize,
      lengthOf: this.staffPageSize,
      search: this.staffSearchTerm,
      sort: order,
    };

    const headers = new HttpHeaders()
      .append('Content-Type', 'application/json')
      .append('authToken', this.getAuthToken);

    this.http.post(AppSettings.API_ENDPOINT + '/stafflist/searchStaff', body, { headers }).subscribe(
      (response: any) => {
        this.staff = response?.data || [];
        this.staffTotalRecords = response?.recordsFiltered ?? response?.recordsTotal ?? this.staff.length;
        this.loadingStaff = false;
      },
      () => {
        this.staff = [];
        this.staffTotalRecords = 0;
        this.loadingStaff = false;
      }
    );
  }

  trackByStaffRowId(_index: number, row: StaffRow): any {
    return row.staffId;
  }

  // ------------------------------------------------------------------
  // Print ID (staff) - self-contained bulk ID card feature, no dependency
  // on header-v2's floating buttons/shared DOM trigger. Reuses the same
  // HeaderService methods header-v2.component.ts's openStaffIdCard()/
  // getStaffTemplateIdCard()/generateStaffIdCard() call, exactly mirroring
  // the native student ID card builder above (openIdCardBuilder() etc.)
  // ------------------------------------------------------------------
  selectedStaffIdsForPrint: number[] = [];
  openStaffIdCardPopup = false;
  isFetchingStaffIdCard = false;
  isGeneratingStaffId = false;
  isStaffIdCardGenerated = false;
  staffIdCardTemplates: any[] = [];

  isStaffSelected(row: StaffRow): boolean {
    return this.selectedStaffIdsForPrint.indexOf(row.staffId as number) !== -1;
  }

  toggleStaffSelection(row: StaffRow, checked: boolean): void {
    const staffId = row.staffId;
    if (staffId === undefined || staffId === null) {
      return;
    }
    if (checked) {
      if (this.selectedStaffIdsForPrint.indexOf(staffId) === -1) {
        this.selectedStaffIdsForPrint.push(staffId);
      }
    } else {
      const idx = this.selectedStaffIdsForPrint.indexOf(staffId);
      if (idx !== -1) {
        this.selectedStaffIdsForPrint.splice(idx, 1);
      }
    }
  }

  get allStaffSelected(): boolean {
    return this.staff.length > 0 && this.staff.every((row) => this.isStaffSelected(row));
  }

  toggleSelectAllStaff(checked: boolean): void {
    for (const row of this.staff) {
      this.toggleStaffSelection(row, checked);
    }
  }

  clearStaffSelection(): void {
    this.selectedStaffIdsForPrint = [];
  }

  async openStaffIdCardBuilder(): Promise<void> {
    if (this.selectedStaffIdsForPrint.length === 0) {
      return;
    }
    this.openStaffIdCardPopup = true;
    this.isFetchingStaffIdCard = true;
    try {
      const response: any = await firstValueFrom(this.headerservice.getStaffIdCardTemplate());
      this.staffIdCardTemplates = response?.status ? (response.body?.idCardTemplateList || []) : [];
    } catch (error) {
      this.staffIdCardTemplates = [];
    }
    this.isFetchingStaffIdCard = false;
  }

  closeStaffIdCardBuilder(): void {
    this.openStaffIdCardPopup = false;
    this.isStaffIdCardGenerated = false;
    this.staffIdCardTemplates = [];
  }

  selectStaffIdCardTemplate(templateId: any): void {
    this.staffIdCardTemplates = this.staffIdCardTemplates.map((tpl) => ({
      ...tpl,
      isSelected: tpl.templateId === templateId,
    }));
  }

  async generateStaffIdCardForSelection(): Promise<void> {
    const selectedTemplate = this.staffIdCardTemplates.find((tpl) => tpl.isSelected);
    if (!selectedTemplate) {
      customToastr('Please select a template first', 'error');
      return;
    }
    this.isGeneratingStaffId = true;
    try {
      const body = {
        lang: 'en',
        staffIdList: this.selectedStaffIdsForPrint.map((id) => String(id)),
        academicId: this.currentAcademicId,
        templateId: selectedTemplate.templateId,
      };
      const response: any = await firstValueFrom(this.headerservice.generateStaffIdCard(body));
      if (response?.status) {
        this.isStaffIdCardGenerated = true;
      }
    } catch (error: any) {
      this.isStaffIdCardGenerated = false;
      customToastr(error?.error?.message || 'Something went wrong', 'error');
    }
    this.isGeneratingStaffId = false;
  }

  // Matches header-v2.component.ts's navigateToStaffIdCardPage() - the ID
  // card "preview" is the same shared jobs-list page (/id-card/staff) that
  // lists every previously-generated batch, not an inline preview.
  viewGeneratedStaffIdCards(): void {
    this.closeStaffIdCardBuilder();
    this.clearStaffSelection();
    this.router.navigate(['/id-card', 'staff']);
  }

  // ------------------------------------------------------------------
  // View / Edit Staff (single popup, mirrors groups.component.ts's
  // view-offcanvas -> edit-offcanvas flow, folded into one form since the
  // legacy "view" panel is read-only and only exists to launch "edit").
  // Deactivate is a field on this same form (Account Status radio +
  // reason), matching groupsStaffUpdate()'s staffBlockStatus/reason params
  // - there is no separate deactivate popup for staff.
  // ------------------------------------------------------------------
  openStaffPopup = false;
  sendingStaffPassword = false;
  staffPopupLoading = false;
  staffPopupSaving = false;
  staffPopupData: any = {};
  invalidStaffPopupMobileNumber = false;
  staffPopupCountry?: Country;
  staffPopupDOBDate: Date | null = null;
  staffPopupDOJDate: Date | null = null;

  // Matches admission-v2.component.ts's formatDateToDDMMYYYY - the p-calendar
  // gives a Date object, but every downstream field/payload in this app
  // works off a DD/MM/YYYY display string.
  private formatDateToDDMMYYYY(date: Date | null): string {
    if (!date) {
      return '';
    }
    const day = ('0' + date.getDate()).slice(-2);
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  // Reverse of the above, for pre-filling the calendar from a date already on
  // the record - so it shows the correct pre-selected date instead of forcing
  // the user to re-pick it on every edit. The backend returns this as either
  // a plain YYYY-MM-DD string or a full ISO datetime (e.g.
  // "2002-06-14T00:00:00.000Z"), so only the date portion is used.
  private parseSqlDateToDate(sqlDate: string): Date | null {
    if (!sqlDate || sqlDate === '0000-00-00') {
      return null;
    }
    const datePart = sqlDate.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    if (!year || !month || !day) {
      return null;
    }
    return new Date(year, month - 1, day);
  }

  // Converts a DD/MM/YYYY display string to the YYYY-MM-DD shape the
  // backend expects - mirrors groups.component.ts's updateStaffDetails()/
  // addNewStaffDetail() ("date[2]-date[1]-date[0]") exactly.
  private toBackendDate(ddmmyyyy: string): string {
    if (!ddmmyyyy) {
      return '';
    }
    const parts = ddmmyyyy.split('/');
    if (parts.length !== 3) {
      return '';
    }
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }

  onStaffDOBSelect(date: Date): void {
    this.staffPopupDOBDate = date;
    this.staffPopupData.staffDOB = this.formatDateToDDMMYYYY(date);
  }

  onStaffDOJSelect(date: Date): void {
    this.staffPopupDOJDate = date;
    this.staffPopupData.staffDOJ = this.formatDateToDDMMYYYY(date);
  }

  openStaffDetail(row: StaffRow): void {
    this.staffPopupData = {};
    this.staffPopupDOBDate = null;
    this.staffPopupDOJDate = null;
    this.invalidStaffPopupMobileNumber = false;
    this.staffPopupLoading = true;
    this.openStaffPopup = true;
    this.groupservice.groupsStaffListDetail(row.staffId, this.getAuthToken).subscribe(
      (data: any) => {
        const detail = data.staffDetail || {};
        this.staffPopupDOBDate = this.parseSqlDateToDate(detail.dateOfBirth);
        this.staffPopupDOJDate = this.parseSqlDateToDate(detail.dateOfJoining);
        this.staffAadharFileType = detail.aadharCopy ? this.commonFunction.getFileTypeFromUrl(detail.aadharCopy) : '';
        this.staffPopupData = {
          staffId: row.staffId,
          staffImage: detail.staffImage ?? '',
          staffName: detail.staffName ?? row.staffName,
          empId: detail.employmentId ?? row.employmentId,
          designation: detail.designation ?? row.designation,
          designationId: detail.designationId ?? null,
          qualification: detail.Qualification ?? '',
          qualificationId: detail.fkStaffQualificationId ?? null,
          staffDOB: this.formatDateToDDMMYYYY(this.staffPopupDOBDate),
          staffDOJ: this.formatDateToDDMMYYYY(this.staffPopupDOJDate),
          gender: detail.gender ?? null,
          mobileNumber: detail.mobileNumber ?? row.mobileNumber,
          mobileCode: detail.countryCode ?? this.brand.defaultCountryDialCode ?? '91',
          emailId: detail.emailId ?? row.emailId,
          address: detail.address ?? '',
          aadharNumber: detail.aadharNumber ?? '',
          aadharCopy: detail.aadharCopy ?? '',
          isAppUser: detail.isAppUser ?? 1,
          isBlocked: detail.isBlocked ?? 0,
          reason: '',
        };
        this.staffPopupCountry = this.findCountryByDialCode(this.staffPopupData.mobileCode);
        this.staffPopupLoading = false;
      },
      () => {
        this.staffPopupLoading = false;
      }
    );
  }

  closeStaffPopup(): void {
    this.openStaffPopup = false;
    this.staffPopupData = {};
  }

  /* Update Staff popup's mobile number field - uses an-country-code-selector's
     <ngx-country-select> instead of jQuery's intlTelInput, matching Contact
     Details' and Add Staff's pattern. */
  onStaffPopupCountryChange(country: Country): void {
    this.staffPopupCountry = country;
    this.staffPopupData.mobileCode = this.toDialCodeString(country);
    this.onStaffPopupMobileNumberInput(this.staffPopupData.mobileNumber);
  }

  onStaffPopupMobileNumberInput(value: string): void {
    this.staffPopupData.mobileNumber = (value || '').replace(/[^0-9]/g, '');
    this.invalidStaffPopupMobileNumber = !this.isValidContactNumber(this.staffPopupData.mobileNumber, this.staffPopupCountry);
  }

  private staffPhotoValidateFile(file: File): boolean {
    const extension = (file.name.split('.').pop() || '').toLowerCase();
    const allowed = ['jpeg', 'png', 'jpg'];
    if (allowed.indexOf(extension) === -1) {
      customToastr('Please select a valid image type of jpeg, png or jpg', 'error');
      return false;
    }
    if (file.size > (1024 * 1024 * 20)) {
      customToastr('Image size should not exceed 20 MB', 'error');
      return false;
    }
    return true;
  }

  // Matches groups.component.ts's validateImage() allowed types for
  // certificate-style uploads (aadhar copy etc.) - wider than the profile
  // photo validator since gif/pdf are valid here too.
  private staffDocumentValidateFile(file: File): boolean {
    const extension = (file.name.split('.').pop() || '').toLowerCase();
    const allowed = ['gif', 'jpeg', 'png', 'jpg', 'pdf'];
    if (allowed.indexOf(extension) === -1) {
      customToastr('Please select a valid image type of gif, jpeg, png, jpg or pdf', 'error');
      return false;
    }
    if (file.size > (1024 * 1024 * 20)) {
      customToastr('Image size should not exceed 20 MB', 'error');
      return false;
    }
    return true;
  }

  staffPopupUploadingPhoto = false;

  onStaffPhotoSelected(event: any): void {
    if (!event.target.files || !event.target.files[0]) {
      return;
    }
    const file = event.target.files[0];
    if (!this.staffPhotoValidateFile(file)) {
      event.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.staffPopupData.staffImage = e.target.result;
    };
    reader.readAsDataURL(file);

    this.staffPopupUploadingPhoto = true;
    const formData = new FormData();
    formData.append('image', file);
    const headers = new HttpHeaders().append('authToken', this.getAuthToken);
    this.http.post(
      AppSettings.API_ENDPOINT + '/upload/image?userType=STAFF&userId=' + this.staffPopupData.staffId,
      formData,
      { headers }
    ).subscribe(
      (data: any) => {
        this.staffPopupUploadingPhoto = false;
        if (data && data.status && data.processedImageUrl) {
          this.staffPopupData.staffImage = data.processedImageUrl[0] ?? data.processedImageUrl;
          customToastr('Staff profile image updated successfully', 'success');
        } else {
          customToastr('Failed to update staff profile image, please try again', 'error');
        }
      },
      () => {
        this.staffPopupUploadingPhoto = false;
        customToastr('Failed to update staff profile image, please try again', 'error');
      }
    );
  }

  removeStaffPhoto(): void {
    this.staffPopupUploadingPhoto = true;
    this.groupservice.removeStaffProfilePic(this.getAuthToken, this.staffPopupData.staffId).subscribe(
      (data: any) => {
        this.staffPopupUploadingPhoto = false;
        if (data && data.errorcode) {
          customToastr('Failed to delete staff image, please try again', 'error');
        } else {
          this.staffPopupData.staffImage = '';
          customToastr('Staff profile image deleted successfully', 'success');
        }
      },
      () => {
        this.staffPopupUploadingPhoto = false;
        customToastr('Failed to delete staff image, please try again', 'error');
      }
    );
  }

  // Aadhar copy upload - same /upload/image?userType=STAFF&userId=... endpoint
  // as the profile photo, just with the file under the "aadharReference"
  // field instead of "image" (see groups.component.ts's
  // startUploadingEditStaffAadharCopy()). Uploads immediately on select,
  // same as the profile photo does.
  staffPopupUploadingAadharCopy = false;
  staffAadharFileType = '';

  onStaffAadharCopySelected(event: any): void {
    if (!event.target.files || !event.target.files[0]) {
      return;
    }
    const file = event.target.files[0];
    if (!this.staffDocumentValidateFile(file)) {
      event.target.value = '';
      return;
    }
    this.staffAadharFileType = file.type;
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.staffPopupData.aadharCopy = e.target.result;
    };
    reader.readAsDataURL(file);

    this.staffPopupUploadingAadharCopy = true;
    const formData = new FormData();
    formData.append('aadharReference', file);
    const headers = new HttpHeaders().append('authToken', this.getAuthToken);
    this.http.post(
      AppSettings.API_ENDPOINT + '/upload/image?userType=STAFF&userId=' + this.staffPopupData.staffId,
      formData,
      { headers }
    ).subscribe(
      (data: any) => {
        this.staffPopupUploadingAadharCopy = false;
        if (data && data.status) {
          customToastr(this.translate.instant('groupsV2.nationalIdCopyUpdatedSuccessfully', { label: this.nationalIdCopyLabel }), 'success');
        } else {
          customToastr(this.translate.instant('groupsV2.failedToUpdateNationalIdCopy', { label: this.nationalIdCopyLabel }), 'error');
        }
      },
      () => {
        this.staffPopupUploadingAadharCopy = false;
        customToastr(this.translate.instant('groupsV2.failedToUpdateNationalIdCopy', { label: this.nationalIdCopyLabel }), 'error');
      }
    );
  }

  saveStaffDetail(): void {
    if (!this.staffPopupData.staffName || !this.staffPopupData.staffName.trim()) {
      customToastr('Please enter staff name', 'error');
      return;
    }
    if (this.staffPopupData.isBlocked === 1 && !this.staffPopupData.reason) {
      customToastr('Please provide a reason for deactivating this staff', 'error');
      return;
    }
    if (!this.formValidator.validateIsData(this.staffPopupData.empId)) {
      customToastr('Please enter staff employee ID', 'error');
      return;
    }
    if (!this.formValidator.validateIsEmail(this.staffPopupData.emailId)) {
      customToastr('Please fill in staff email ID in correct format', 'error');
      return;
    }
    if (!this.formValidator.validateIsData(this.staffPopupData.address)) {
      customToastr('Please fill in staff address', 'error');
      return;
    }
    if (this.formValidator.validateIsData(this.staffPopupData.aadharNumber) && !/^[0-9]{12}$/.test(this.staffPopupData.aadharNumber)) {
      customToastr(this.translate.instant('groupsV2.invalidNationalIdNumber', { label: this.translate.instant(this.brand.nationalIdFieldLabelKey) }), 'error');
      return;
    }
    if (!this.staffPopupData.mobileNumber || this.invalidStaffPopupMobileNumber) {
      customToastr('Please enter valid staff mobile number', 'error');
      return;
    }
    const staffDOBError = this.formValidator.validateStaffDOB(this.staffPopupData.staffDOB);
    if (staffDOBError) {
      customToastr(staffDOBError, 'error');
      return;
    }
    const staffDatesError = this.formValidator.validateDates(this.staffPopupData.staffDOB, this.staffPopupData.staffDOJ);
    if (staffDatesError) {
      customToastr(staffDatesError, 'error');
      return;
    }
    this.staffPopupSaving = true;
    this.groupservice.groupsStaffUpdate(
      this.staffPopupData.staffName,
      this.toBackendDate(this.staffPopupData.staffDOB),
      this.staffPopupData.gender,
      this.staffPopupData.isAppUser,
      this.staffPopupData.isBlocked,
      this.staffPopupData.qualificationId,
      this.staffPopupData.designationId,
      this.staffPopupData.empId,
      this.toBackendDate(this.staffPopupData.staffDOJ),
      this.staffPopupData.mobileNumber,
      this.staffPopupData.mobileCode,
      this.staffPopupData.emailId,
      this.staffPopupData.address,
      this.staffPopupData.staffId,
      this.staffPopupData.reason,
      this.staffPopupData.aadharNumber,
      this.getAuthToken
    ).subscribe(
      (data: any) => {
        this.staffPopupSaving = false;
        if (data && data.update === true) {
          customToastr('Staff updated successfully', 'success');
          this.closeStaffPopup();
          this.fetchStaff();
        } else {
          customToastr(data?.message || 'Something went wrong, please try again', 'error');
        }
      },
      () => {
        this.staffPopupSaving = false;
        customToastr('Something went wrong, please try again', 'error');
      }
    );
  }

  async sendStaffPassword(): Promise<void> {
    const staffId = this.staffPopupData?.staffId;
    if (!staffId || this.sendingStaffPassword) {
      return;
    }
    this.sendingStaffPassword = true;
    try {
      const headers = new HttpHeaders().set('token', this.getAuthToken);
      const response: any = await firstValueFrom(
        this.http.post(AppSettings.API_ENDPOINT_V3 + apiConstants.sendStaffPasswordOnEmail, {
          staffId,
          lang: 'en',
        }, { headers })
      );
      if (response?.status) {
        customToastr(response.message || 'Password sent to staff\'s email', 'success');
      } else {
        customToastr(response?.message || 'Unable to send password, please try again', 'error');
      }
    } catch (error: any) {
      customToastr(error?.error?.message || 'Unable to send password, please try again', 'error');
    } finally {
      this.sendingStaffPassword = false;
    }
  }

  // ------------------------------------------------------------------
  // Add Staff
  // ------------------------------------------------------------------
  addStaffMenuOpen = false;

  toggleAddStaffMenu(): void {
    this.addStaffMenuOpen = !this.addStaffMenuOpen;
  }

  openAddStaffPopup = false;
  addStaffSaving = false;
  addStaffData: any = {};
  invalidAddStaffMobileNumber = false;
  addStaffCountry?: Country;

  openDesignationPopup(): void {
    this.closeActionMenu();
    this.resetDesignationForm();
    this.openManageDesignationPopup = true;
  }

  openQualificationPopup(): void {
    this.closeActionMenu();
    this.resetQualificationForm();
    this.getStaffQualificationList();
    this.openManageQualificationPopup = true;
  }

  addStaffSelectedFile: File | null = null;
  addStaffPhotoPreview = '';

  addStaffDOBDate: Date | null = null;
  addStaffDOJDate: Date | null = null;

  onAddStaffDOBSelect(date: Date): void {
    this.addStaffDOBDate = date;
    this.addStaffData.dateOfBirth = this.formatDateToDDMMYYYY(date);
  }

  onAddStaffDOJSelect(date: Date): void {
    this.addStaffDOJDate = date;
    this.addStaffData.dateOfJoining = this.formatDateToDDMMYYYY(date);
  }

  openAddStaffForm(): void {
    this.closeActionMenu();
    this.addStaffData = {
      staffName: '',
      empId: '',
      dateOfBirth: '',
      gender: 1,
      isAppUser: 0,
      isBlocked: 0,
      qualificationId: null,
      designationId: null,
      dateOfJoining: '',
      mobileNumber: '',
      countryCode: this.brand.defaultCountryDialCode || '91',
      emailId: '',
      address: '',
      aadharNumber: '',
    };
    this.addStaffDOBDate = null;
    this.addStaffDOJDate = null;
    this.invalidAddStaffMobileNumber = false;
    this.addStaffCountry = this.countryCodeService.getByIso2(this.brand.defaultCountryIso2Upper || 'IN');
    this.addStaffSelectedFile = null;
    this.addStaffPhotoPreview = '';
    this.addStaffSelectedAadharFile = null;
    this.addStaffAadharPreviewName = '';
    this.openAddStaffPopup = true;
  }

  /* Add Staff popup's mobile number field - uses an-country-code-selector's
     <ngx-country-select> instead of jQuery's intlTelInput, matching Contact
     Details' pattern. */
  onAddStaffCountryChange(country: Country): void {
    this.addStaffCountry = country;
    this.addStaffData.countryCode = this.toDialCodeString(country);
    this.onAddStaffMobileNumberInput(this.addStaffData.mobileNumber);
  }

  onAddStaffMobileNumberInput(value: string): void {
    this.addStaffData.mobileNumber = (value || '').replace(/[^0-9]/g, '');
    this.invalidAddStaffMobileNumber = !this.isValidContactNumber(this.addStaffData.mobileNumber, this.addStaffCountry);
  }

  closeAddStaffPopup(): void {
    this.openAddStaffPopup = false;
    this.addStaffSelectedFile = null;
    this.addStaffPhotoPreview = '';
    this.addStaffSelectedAadharFile = null;
    this.addStaffAadharPreviewName = '';
  }

  onAddStaffPhotoSelected(event: any): void {
    if (!event.target.files || !event.target.files[0]) {
      return;
    }
    const file = event.target.files[0];
    if (!this.staffPhotoValidateFile(file)) {
      event.target.value = '';
      return;
    }
    this.addStaffSelectedFile = file;
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.addStaffPhotoPreview = e.target.result;
    };
    reader.readAsDataURL(file);
  }

  removeAddStaffPhoto(): void {
    this.addStaffSelectedFile = null;
    this.addStaffPhotoPreview = '';
  }

  addStaffSelectedAadharFile: File | null = null;
  addStaffAadharPreviewName = '';

  onAddStaffAadharCopySelected(event: any): void {
    if (!event.target.files || !event.target.files[0]) {
      return;
    }
    const file = event.target.files[0];
    if (!this.staffDocumentValidateFile(file)) {
      event.target.value = '';
      return;
    }
    this.addStaffSelectedAadharFile = file;
    this.addStaffAadharPreviewName = file.name;
  }

  previewAddStaffAadharCopy(): void {
    if (!this.addStaffSelectedAadharFile) {
      return;
    }
    const objectUrl = URL.createObjectURL(this.addStaffSelectedAadharFile);
    this.docPreviewObjectUrl = objectUrl;
    this.openDocPreview(objectUrl, this.addStaffSelectedAadharFile.type === 'application/pdf', this.translate.instant(this.brand.nationalIdCopyLabelKey));
  }

  removeAddStaffAadharCopy(): void {
    this.addStaffSelectedAadharFile = null;
    this.addStaffAadharPreviewName = '';
  }

  private uploadAddedStaffPhoto(staffId: number): void {
    if (!this.addStaffSelectedFile && !this.addStaffSelectedAadharFile) {
      return;
    }
    const formData = new FormData();
    if (this.addStaffSelectedFile) {
      formData.append('image', this.addStaffSelectedFile);
    }
    if (this.addStaffSelectedAadharFile) {
      formData.append('aadharReference', this.addStaffSelectedAadharFile);
    }
    const headers = new HttpHeaders().append('authToken', this.getAuthToken);
    this.http.post(
      AppSettings.API_ENDPOINT + '/upload/image?userType=STAFF&userId=' + staffId,
      formData,
      { headers }
    ).subscribe(
      () => {},
      () => {
        customToastr('Staff added, but the photo/aadhar copy failed to upload', 'error');
      }
    );
  }

  submitAddStaff(): void {
    if (!this.addStaffData.staffName || !this.addStaffData.staffName.trim()) {
      customToastr('Please enter staff name', 'error');
      return;
    }
    const staffDOBError = this.formValidator.validateStaffDOB(this.addStaffData.dateOfBirth);
    if (staffDOBError) {
      customToastr(staffDOBError, 'error');
      return;
    }
    if (this.addStaffData.qualificationId === null || this.addStaffData.qualificationId === undefined) {
      customToastr('Please select a qualification', 'error');
      return;
    }
    if (!this.addStaffData.designationId) {
      customToastr('Please select a designation', 'error');
      return;
    }
    if (!this.addStaffData.empId || !this.addStaffData.empId.trim()) {
      customToastr('Please enter employee ID', 'error');
      return;
    }
    if (!this.addStaffData.mobileNumber || this.invalidAddStaffMobileNumber) {
      customToastr('Please enter a valid mobile number', 'error');
      return;
    }
    if (!this.formValidator.validateIsData(this.addStaffData.emailId) || !this.formValidator.validateIsEmail(this.addStaffData.emailId)) {
      customToastr('Please enter a valid email address', 'error');
      return;
    }
    if (!this.addStaffData.address || !this.addStaffData.address.trim()) {
      customToastr('Please enter address', 'error');
      return;
    }
    if (this.addStaffData.aadharNumber && !/^[0-9]{12}$/.test(this.addStaffData.aadharNumber)) {
      customToastr(this.translate.instant('groupsV2.invalidNationalIdNumber', { label: this.translate.instant(this.brand.nationalIdFieldLabelKey) }), 'error');
      return;
    }
    const staffDatesError = this.formValidator.validateDates(this.addStaffData.dateOfBirth, this.addStaffData.dateOfJoining);
    if (staffDatesError) {
      customToastr(staffDatesError, 'error');
      return;
    }
    this.addStaffSaving = true;
    this.groupservice.groupsAddNewStaff(
      this.addStaffData.staffName,
      this.addStaffData.empId,
      this.toBackendDate(this.addStaffData.dateOfBirth),
      `${this.addStaffData.gender}`,
      this.addStaffData.isAppUser,
      this.addStaffData.isBlocked,
      `${this.addStaffData.qualificationId}`,
      `${this.addStaffData.qualificationId}`,
      `${this.addStaffData.designationId}`,
      this.toBackendDate(this.addStaffData.dateOfJoining),
      this.addStaffData.mobileNumber,
      `${this.addStaffData.countryCode}`,
      this.addStaffData.emailId,
      this.addStaffData.address,
      this.addStaffData.aadharNumber,
      this.getSchoolId,
      this.getAuthToken
    ).subscribe(
      (data: any) => {
        this.addStaffSaving = false;
        if (data && data.addStaff === true) {
          if (data.staffId) {
            this.uploadAddedStaffPhoto(data.staffId);
          }
          customToastr('Staff added successfully', 'success');
          this.closeAddStaffPopup();
          this.fetchStaff();
        } else if (data && data.messageText) {
          customToastr(data.messageText, 'error');
        } else {
          customToastr(data?.message || 'Failed to add staff, please try again', 'error');
        }
      },
      () => {
        this.addStaffSaving = false;
        customToastr('Something went wrong, please try again', 'error');
      }
    );
  }

  // ------------------------------------------------------------------
  // Manage Designation - native groups-v2 popup mirroring groups.component.ts's
  // #offcanvas-staff-desgnation (add form + editable list), same access-level
  // checkboxes/API params (isSuperAdmin/isFinanceAdmin/isFeeDefaulterAdmin/
  // isClassTeacher/isDriver).
  // ------------------------------------------------------------------
  openManageDesignationPopup = false;
  designationSaving = false;
  designationEditingId: number | null = null;
  designationForm: any = {};

  private resetDesignationForm(): void {
    this.designationEditingId = null;
    this.designationForm = {
      name: '',
      isSuperAdmin: 0,
      isFinanceAdmin: 0,
      isFeeDefaulterAdmin: 0,
      isClassTeacher: 0,
      isDriver: 0,
    };
  }

  closeManageDesignationPopup(): void {
    this.openManageDesignationPopup = false;
  }

  editDesignationRow(designation: DesignationItem & { [key: string]: any }): void {
    this.designationEditingId = designation.designationId;
    this.designationForm = {
      name: designation.designation,
      isSuperAdmin: designation.isSuperAdmin ? 1 : 0,
      isFinanceAdmin: designation.isFinanceAdmin ? 1 : 0,
      isFeeDefaulterAdmin: designation.isFeeDefaulterAdmin ? 1 : 0,
      isClassTeacher: designation.isClassTeacher ? 1 : 0,
      isDriver: designation.isDriver ? 1 : 0,
    };
  }

  cancelEditDesignationRow(): void {
    this.resetDesignationForm();
  }

  submitDesignationForm(): void {
    if (!this.designationForm.name || !this.designationForm.name.trim()) {
      customToastr('Please provide a valid designation name', 'error');
      return;
    }
    this.designationSaving = true;
    if (this.designationEditingId) {
      this.groupservice.groupsStaffEditDesignation(
        this.designationEditingId,
        this.designationForm.name,
        this.designationForm.isSuperAdmin,
        this.designationForm.isFeeDefaulterAdmin,
        this.designationForm.isFinanceAdmin,
        this.designationForm.isClassTeacher,
        this.designationForm.isDriver,
        this.getAuthToken
      ).subscribe(
        (data: any) => {
          this.designationSaving = false;
          if (data && data.status) {
            customToastr('Designation updated successfully', 'success');
            this.resetDesignationForm();
            this.getSchoolDesignationList();
          } else {
            customToastr(data?.message || 'Failed to update designation, please try again', 'error');
          }
        },
        () => {
          this.designationSaving = false;
          customToastr('Failed to update designation, please try again', 'error');
        }
      );
    } else {
      this.groupservice.groupsStaffAdddesignation(
        this.getSchoolId,
        this.designationForm.name,
        this.designationForm.isSuperAdmin,
        this.designationForm.isFeeDefaulterAdmin,
        this.designationForm.isFinanceAdmin,
        this.designationForm.isClassTeacher,
        this.designationForm.isDriver,
        this.getAuthToken
      ).subscribe(
        (data: any) => {
          this.designationSaving = false;
          if (data && data.addDesignation === true) {
            customToastr('Designation added successfully', 'success');
            this.resetDesignationForm();
            this.getSchoolDesignationList();
          } else {
            customToastr(data?.message || 'Failed to add designation, please try again', 'error');
          }
        },
        () => {
          this.designationSaving = false;
          customToastr('Failed to add designation, please try again', 'error');
        }
      );
    }
  }

  // ------------------------------------------------------------------
  // Manage Qualification - native groups-v2 popup mirroring groups.component.ts's
  // #offcanvas-staff-qualification (add form + editable list).
  // ------------------------------------------------------------------
  openManageQualificationPopup = false;
  qualificationSaving = false;
  qualificationLoading = false;
  qualificationEditingId: number | null = null;
  qualificationName = '';
  staffQualificationList: any[] = [];

  private resetQualificationForm(): void {
    this.qualificationEditingId = null;
    this.qualificationName = '';
  }

  closeManageQualificationPopup(): void {
    this.openManageQualificationPopup = false;
  }

  getStaffQualificationList(): void {
    this.qualificationLoading = true;
    this.groupservice.groupsStaffQualifList(this.getSchoolId, this.getAuthToken).subscribe(
      (data: any) => {
        this.staffQualificationList = data.staffQualification || [];
        this.qualificationLoading = false;
      },
      () => {
        this.qualificationLoading = false;
      }
    );
  }

  editQualificationRow(qualification: any): void {
    this.qualificationEditingId = qualification.pkStaffQualificationId;
    this.qualificationName = qualification.Qualification;
  }

  cancelEditQualificationRow(): void {
    this.resetQualificationForm();
  }

  submitQualificationForm(): void {
    if (!this.qualificationName || !this.qualificationName.trim()) {
      customToastr('Please provide a valid qualification name', 'error');
      return;
    }
    this.qualificationSaving = true;
    if (this.qualificationEditingId) {
      this.groupservice.groupsStaffEditQualification(
        this.qualificationEditingId,
        this.qualificationName,
        this.getAuthToken
      ).subscribe(
        (data: any) => {
          this.qualificationSaving = false;
          if (data && data.status) {
            customToastr('Qualification updated successfully', 'success');
            this.resetQualificationForm();
            this.getStaffQualificationList();
          } else {
            customToastr(data?.message || 'Failed to update qualification, please try again', 'error');
          }
        },
        () => {
          this.qualificationSaving = false;
          customToastr('Failed to update qualification, please try again', 'error');
        }
      );
    } else {
      this.groupservice.groupsStaffAddqualification(
        this.getSchoolId,
        this.qualificationName,
        this.getAuthToken
      ).subscribe(
        (data: any) => {
          this.qualificationSaving = false;
          if (data && data.addQualification === true) {
            customToastr('Qualification added successfully', 'success');
            this.resetQualificationForm();
            this.getStaffQualificationList();
          } else {
            customToastr(data?.message || 'Failed to add qualification, please try again', 'error');
          }
        },
        () => {
          this.qualificationSaving = false;
          customToastr('Failed to add qualification, please try again', 'error');
        }
      );
    }
  }
}
